import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Alert from '@mui/material/Alert';
import { fetchTransactions, addTransaction, updateTransaction, deleteTransaction } from '../slices/transactionSlice';
import { addIncome, addExpense, fetchIncomes, fetchExpenses } from '../slices/recurringSlice';
import apiClient from '../services/api';
import merchantRuleService from '../services/merchantRuleService';
import categoryService from '../services/categoryService';
import { formatAmount as formatAmountRaw } from '../utils/formatHelpers';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryEmoji, buildEmojiOverrides } from '../utils/categoryEmoji';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { detectarCandidatosRecurrentes, buildRecurringPayloadDesdeTransaccion } from '../utils/recurringDetection';
import RecurringCandidatesDialog from '../components/RecurringCandidatesDialog';
// PCM opera en Soles (Perú) — se antepone el símbolo a los montos que se
// muestran en esta pantalla (antes se mostraban sin ningún símbolo).
const formatAmount = (n) => `S/ ${formatAmountRaw(n)}`;
// 5 ago 2026 (Augusto: un cargo en dólares se importó/mostraba como si
// fueran soles): la gran mayoría de transacciones sí están en soles, pero
// una importada de un estado de cuenta puede traer su propia moneda
// cuando difiere de la cuenta/tarjeta vinculada (ver
// moneda_override/Transaction.moneda en el backend). Si tx.moneda viene
// con algo, se usa ese símbolo en vez del "S/" fijo de siempre.
// 7 sep 2026: recibe la moneda ya resuelta (ver monedaDeTx dentro del
// componente) en vez de leer tx.moneda directo -- así un movimiento sin
// moneda de una cuenta en dólares muestra "$" y no "S/".
const formatTxAmount = (tx, moneda) => `${getCurrencySymbol(moneda || 'PEN')} ${formatAmountRaw(tx.monto)}`;
// FIX: TransactionList.js (el componente viejo montado en /transactions)
// no tenía ningún formulario para agregar una transacción — solo
// listaba y delegaba "editar" a un onEdit que routes.js nunca conectaba
// a nada. Además leía transaction.transaction_type/amount/date/source,
// campos que no existen en el backend (tipo/monto/fecha/categoria). El
// resultado: la pantalla de Transacciones no se podía usar en absoluto.
// Esta página reemplaza ese componente con el mismo patrón de diálogo
// que Cuentas y Deudas, usando los campos reales del backend y
// permitiendo asociar la transacción a una cuenta existente.
const categorias = [
  'Salario', 'Alquiler', 'Alimentación', 'Transporte', 'Servicios',
  'Entretenimiento', 'Salud', 'Educación', 'Otro',
];
// 19 ago 2026 (pedido de Augusto: "no debería permitir entrar otra si ya
// existe, no importa si es un tema de mayúsculas o minúsculas o si hay
// problemas ortográficos"): mismo criterio de normalización que
// resolve_categoria/_normalizar_categoria en el backend
// (endpoints/merchant_rules.py) -- NFC primero (unifica formas Unicode
// equivalentes de una misma tilde), después NFKD + remover las marcas
// diacríticas combinantes (quita tildes/diéresis), un barrido de
// caracteres invisibles comunes (espacio de ancho cero, marcador de
// unión, BOM -- pueden colarse en texto pegado desde otras fuentes y
// hacer que dos categorías se vean idénticas sin serlo a nivel de
// bytes), minúsculas, y espacios repetidos colapsados.
//
// 20 ago 2026: la primera versión de esta función escribía el rango de
// tildes con los caracteres Unicode combinantes literales dentro del
// regex (en vez de su forma escapada \uXXXX) -- al pasar el código por un
// editor web (copiar/pegar en GitHub) esos caracteres, al ser
// básicamente invisibles, corren el riesgo de corromperse o perderse en
// el camino. Se reescribe con escapes \uXXXX (texto ASCII puro en el
// código fuente, inmune a esa clase de problema).
//
// Se usa para DECIDIR si dos categorías son "la misma" al armar
// `allCategorias` más abajo -- el texto que ve el usuario no cambia, solo
// cuál de las variantes ya guardadas se usa como representante único en
// el dropdown.
const normalizarCategoria = (s) => {
  if (!s) return '';
  const nfc = s.normalize('NFC');
  const sinTildes = nfc.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const sinInvisibles = sinTildes.replace(/[\u200b\u200c\u200d\ufeff\u00ad]/g, '');
  return sinInvisibles.trim().toLowerCase().replace(/\s+/g, ' ');
};
// 3 ago 2026 (pedido de Augusto): las transacciones importadas del
// Importador de Estados de Cuenta guardan la categoría "base" tal como la
// devuelve la IA -- en inglés (food/transport/bills/..., ver
// EXTRACTION_SYSTEM_PROMPT en backend_new/app/api/v1/endpoints/statements.py),
// mientras que las categorías creadas a mano en esta página (ver
// `categorias` arriba) ya están en español. El resultado era una tabla
// mezclando "food" con "Alimentación" en la misma columna. Este mapa
// traduce las categorías base conocidas al idioma actual de la interfaz;
// cualquier categoría personalizada (ej. "mascotas", creada por el
// usuario o sugerida libremente por la IA) se deja tal cual, porque no
// hay forma confiable de traducir texto libre.
const CATEGORIA_BASE_LABELS = {
  food: { es: 'Alimentación', en: 'Food & Dining' },
  transport: { es: 'Transporte', en: 'Transport' },
  bills: { es: 'Facturas y Servicios', en: 'Bills & Utilities' },
  shopping: { es: 'Compras', en: 'Shopping' },
  entertainment: { es: 'Entretenimiento', en: 'Entertainment' },
  salary: { es: 'Salario', en: 'Salary' },
  other: { es: 'Otro', en: 'Other' },
  otro: { es: 'Otro', en: 'Other' },
};
const traducirCategoria = (categoria, language) => {
  if (!categoria) return categoria;
  const entry = CATEGORIA_BASE_LABELS[categoria.trim().toLowerCase()];
  if (!entry) return categoria;
  return language === 'en' ? entry.en : entry.es;
};
// 4 ago 2026 (pedido de Augusto): "poner los iconos de categorías
// disponibles para que el usuario los utilice cuando ingresa o edita
// transacciones" -- fila de chips con el emoji de cada categoría base
// (ver utils/categoryEmoji.js) arriba del Autocomplete de Categoría, en
// vez de solo texto. Un click en un chip fija esa categoría directo; el
// Autocomplete de abajo sigue disponible tal cual para categorías
// personalizadas o texto libre.
// 17 sep 2026: `language` se pasa como prop (no via useLanguage() acá
// mismo) porque este es un componente de módulo, no anidado dentro de
// TransactionsPage -- el label de cada chip se traduce para mostrarse,
// pero `cat` (el value real que se guarda) se mantiene crudo en español,
// mismo criterio que en cualquier otro lado de esta página.
const CategoriaPicker = ({ value, onChange, language }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
    {categorias.map((cat) => (
      <Chip
        key={cat}
        icon={<span style={{ fontSize: '0.95rem' }}>{getCategoryEmoji(cat)}</span>}
        label={traducirCategoria(cat, language)}
        size="small"
        clickable
        variant={value === cat ? 'filled' : 'outlined'}
        color={value === cat ? 'primary' : 'default'}
        onClick={() => onChange(cat)}
      />
    ))}
  </Box>
);
// Niveles disponibles para armar subgrupos anidados en la tabla (ej.
// Tipo > Categoría > Proveedor > Mes) — el orden en que el usuario los
// selecciona (ver nivelesAgrupacion) es el orden de anidamiento: el
// primero que elige es el grupo más externo.
// 4 ago 2026 (pedido de Augusto): además de Tipo/Categoría/Proveedor/Mes
// ya existentes, se agregan Cuenta, Moneda y Año como niveles adicionales
// de sub-agrupación — ver claveDeNivel más abajo, que ya tiene acceso a
// `cuentas` (cargadas desde /dashboard/accounts) para resolver el nombre
// y la moneda de cada cuenta.
// 28 ago 2026 (pedido de Augusto: "quiero agrupar con Moneda también...
// que muestre 2 grupos, Dólares y Soles"): "Moneda" sigue siendo un nivel
// de agrupación elegible -- ver claveDeNivel más abajo (usa tx.moneda
// directo, así el grupo refleja la moneda real de cada movimiento, no la
// de la cuenta vinculada). Además, CUALQUIER fila de resumen (el total
// general de arriba, o cada grupo si se agrupa por Categoría/Proveedor/
// etc.) muestra el total en Soles y el total en Dólares juntos en la
// misma línea -- ver totalFiltradoDolares y grupo.totalDolares más abajo.
// 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
// ingles?"): estas listas de opciones se vuelven funciones de `language`
// (llamadas desde dentro del componente, memoizadas con useMemo) en vez
// de constantes de módulo fijas en español -- el VALUE (usado para
// filtrar/agrupar/ordenar) no cambia, solo el label visible.
const makeNivelesDisponibles = (language) => [
  { value: 'tipo', label: language === 'es' ? 'Tipo' : 'Type' },
  { value: 'categoria', label: language === 'es' ? 'Categoría' : 'Category' },
  { value: 'proveedor', label: language === 'es' ? 'Proveedor' : 'Merchant' },
  { value: 'cuenta', label: language === 'es' ? 'Cuenta' : 'Account' },
  { value: 'moneda', label: language === 'es' ? 'Moneda' : 'Currency' },
  { value: 'anio', label: language === 'es' ? 'Fecha (año)' : 'Date (year)' },
  { value: 'mes', label: language === 'es' ? 'Fecha (mes)' : 'Date (month)' },
  // 28 ago 2026 (pedido de Augusto: "¿se puede hacer otra agrupación por
  // fecha de corte de cada tarjeta?"): a diferencia de "Fecha (mes)", que
  // agrupa por mes calendario, esto agrupa cada movimiento según el ciclo
  // de facturación REAL de la tarjeta a la que pertenece (ej. un consumo
  // del 30 de julio puede caer en el ciclo que cierra el 25 de agosto, no
  // en el "grupo de julio") -- ver claveDeNivel más abajo y cuenta.dia_cierre
  // (nuevo campo en Account, completado por el importador de estados de
  // cuenta o a mano en CuentasPage.js).
  { value: 'corte', label: language === 'es' ? 'Fecha de corte' : 'Statement date' },
];
// Opciones para ordenar las transacciones dentro de cada grupo (o la
// tabla completa cuando no hay agrupación).
const makeOpcionesOrden = (language) => [
  { value: 'fecha', label: language === 'es' ? 'Fecha' : 'Date' },
  { value: 'monto', label: language === 'es' ? 'Monto' : 'Amount' },
  { value: 'categoria', label: language === 'es' ? 'Categoría' : 'Category' },
  { value: 'proveedor', label: language === 'es' ? 'Proveedor' : 'Merchant' },
  { value: 'tipo', label: language === 'es' ? 'Tipo' : 'Type' },
];
// 29 ago 2026 (pedido de Augusto: "orden por cada tipo de nivel cuando
// salgan los resultados"): distinto de OPCIONES_ORDEN de arriba -- ese
// ordena las TRANSACCIONES dentro de cada grupo, esto ordena los GRUPOS
// entre sí (ej. con agrupación por "Fecha de corte", elegir "Fecha" acá
// los deja en orden cronológico en vez del orden por monto de siempre).
// Aplica a CUALQUIER nivel de agrupación (Tipo, Categoría, Cuenta, Mes,
// Corte, etc.), no solo a los de fecha -- "Nombre" ordena alfabético y
// "Fecha" usa el movimiento más antiguo de cada grupo como referencia,
// ver fechaOrdenGrupo en construirGrupos.
const makeOpcionesOrdenGrupos = (language) => [
  { value: 'total', label: language === 'es' ? 'Monto' : 'Amount' },
  { value: 'nombre', label: language === 'es' ? 'Nombre' : 'Name' },
  { value: 'fecha', label: language === 'es' ? 'Fecha' : 'Date' },
];
// Frecuencias que se ofrecen al marcar una transacción como recurrente
// (31 jul 2026) — deliberadamente solo estas 3, a pedido de Augusto.
// "Días 15 y 30" es el mismo campo "quincenal" que ya usa el backend
// (ver calendar.py), solo con otro nombre en la interfaz porque así se
// usa en una planilla peruana típica.
const makeFrecuenciasRecurrente = (language) => [
  { value: 'diario', label: language === 'es' ? 'Diario' : 'Daily' },
  // 21 sep 2026 (Augusto: "no aparece frecuencia Semanal"): ver la misma
  // nota en TransactionForm.js.
  { value: 'semanal', label: language === 'es' ? 'Semanal' : 'Weekly' },
  { value: 'quincenal', label: language === 'es' ? 'Días 15 y 30' : 'Days 15 and 30' },
  { value: 'mensual', label: language === 'es' ? 'Mensual' : 'Monthly' },
];
// 28 ago 2026 (pedido de Augusto: agrupando por Moneda salía "PEN (309)"
// como nombre de grupo -- el código ISO de la moneda no dice nada por sí
// solo para alguien que no maneja esa jerga; "Soles (309)" sí). Se usa
// para traducir el código a algo legible en `claveDeNivel` (nivel
// "moneda") -- si aparece un código que no está en este mapa (una moneda
// rara que todavía no se vio), se muestra el código tal cual en vez de
// romper la pantalla.
const NOMBRE_MONEDA = {
  es: {
    PEN: 'Soles', USD: 'Dólares', EUR: 'Euros', GBP: 'Libras esterlinas',
    MXN: 'Pesos mexicanos', COP: 'Pesos colombianos', CLP: 'Pesos chilenos',
    ARS: 'Pesos argentinos', BRL: 'Reales',
  },
  en: {
    PEN: 'Soles', USD: 'Dollars', EUR: 'Euros', GBP: 'British Pounds',
    MXN: 'Mexican Pesos', COP: 'Colombian Pesos', CLP: 'Chilean Pesos',
    ARS: 'Argentine Pesos', BRL: 'Brazilian Reais',
  },
};
// Solo usado para el NOMBRE del grupo al agrupar por Moneda (claveDeNivel
// más abajo) -- puramente decorativo, no se compara ni se guarda en
// ningún lado, así que traducirlo según el idioma actual es seguro.
const nombreMoneda = (codigo, language) => {
  const c = (codigo || '').toUpperCase();
  const tabla = NOMBRE_MONEDA[language === 'es' ? 'es' : 'en'];
  return tabla[c] || c || (language === 'es' ? 'Sin moneda' : 'No currency');
};
const hoyISO = () => new Date().toISOString().slice(0, 10);
// 14 sep 2026 (pedido de Augusto: "cuando el usuario hace refresh en
// transacciones debe de quedarse los filtros donde estaban"): filtros,
// agrupación y orden se guardan en localStorage para sobrevivir un F5 o un
// cierre/apertura de pestaña -- mismo mecanismo que SIDEBAR_OPEN_STORAGE_KEY
// en NotionLayout.js, pero acá se guarda un solo objeto JSON con todo junto
// (son varios campos) en vez de una clave por campo.
const FILTROS_STORAGE_KEY = 'pcm_filtros_transacciones';
const leerFiltrosGuardados = () => {
  try {
    const raw = localStorage.getItem(FILTROS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // localStorage bloqueado (modo privado) o JSON corrupto -- se arranca
    // igual que si no hubiera nada guardado, en vez de romper la pantalla.
    return null;
  }
};
// 17 sep 2026 (Augusto: "necesito poder filtrar por 2 o más valores"):
// los filtros de cuenta, categoría, mes, año y fecha de corte pasaron de
// un valor suelto a una lista. Esta función existe para que los filtros
// que ya estaban guardados en localStorage (strings, del formato viejo)
// no rompan la pantalla al abrirla la primera vez después del cambio --
// un '' guardado se vuelve [], y 'Mercado' se vuelve ['Mercado'].
const comoLista = (valor) => {
  if (Array.isArray(valor)) return valor;
  if (valor === undefined || valor === null || valor === '') return [];
  return [valor];
};

const emptyTx = {
  tipo: 'gasto',
  monto: '',
  categoria: 'Otro',
  proveedor: '',
  descripcion: '',
  account_id: '',
  es_recurrente: false,
  frecuencia: 'mensual',
  dia_mes: String(new Date().getDate()),
};
const TransactionsPage = () => {
  const { language } = useLanguage();
  const NIVELES_DISPONIBLES = useMemo(() => makeNivelesDisponibles(language), [language]);
  const OPCIONES_ORDEN = useMemo(() => makeOpcionesOrden(language), [language]);
  const OPCIONES_ORDEN_GRUPOS = useMemo(() => makeOpcionesOrdenGrupos(language), [language]);
  const frecuenciasRecurrente = useMemo(() => makeFrecuenciasRecurrente(language), [language]);
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions.list);
  const loading = useSelector((state) => state.transactions.loading);
  const error = useSelector((state) => state.transactions.error);
  // 5 ago 2026: recurrentes ya activos, para el detector de candidatos
  // (ver handleCreateTx) -- si el proveedor ya tiene una serie activa, no
  // se le vuelve a sugerir.
  const recurringIncomes = useSelector((state) => state.recurring.incomes);
  const recurringExpenses = useSelector((state) => state.recurring.expenses);
  // 5 ago 2026: ya no se usa state.transactions.summary (ver `resumen`
  // más abajo, calculado con acceso a `cuentas` para excluir abonos a
  // deudas de los Ingresos).
  const [cuentas, setCuentas] = useState([]);
  // 28 ago 2026 (pedido de Augusto): historial de saldo real por fecha de
  // corte (ver backend_new/app/models/account_balance_snapshot.py) -- se
  // usa en construirGrupos/calcularSaldoAcumulado más abajo para anclar el
  // saldo acumulado de cada ciclo de "Fecha de corte" a un número real del
  // banco en vez de reconstruirlo solo de las transacciones cargadas.
  const [balanceSnapshots, setBalanceSnapshots] = useState([]);
  // 28 ago 2026 (pedido de Augusto): alta rápida de cuenta sin salir de
  // Transacciones -- se dispara desde el selector "Cuenta (opcional)" de
  // los diálogos de Agregar/Editar. `cuentaRapidaTarget` recuerda a qué
  // formulario (nuevo o edición) hay que asignarle la cuenta recién
  // creada al terminar.
  const [openNuevaCuentaRapida, setOpenNuevaCuentaRapida] = useState(false);
  const [cuentaRapidaTarget, setCuentaRapidaTarget] = useState('nuevaTx');
  const [nuevaCuentaRapida, setNuevaCuentaRapida] = useState({ nombre: '', tipo: 'ahorros', moneda: 'PEN', balance: '' });
  const [guardandoCuentaRapida, setGuardandoCuentaRapida] = useState(false);
  const [errorCuentaRapida, setErrorCuentaRapida] = useState(null);
  // "Memoria" de proveedor -> categoría (endpoints/merchant_rules.py): se
  // usa para sugerir proveedores ya conocidos en el Autocomplete y para
  // auto-completar la categoría apenas se elige o se escribe un proveedor
  // que ya se había categorizado antes (ej. "Wong" -> "Alimentación").
  const [merchantRules, setMerchantRules] = useState([]);
  // Categorías propias del usuario (endpoints/categories.py) — antes esta
  // página solo ofrecía las 9 fijas de abajo (categorias), sin ninguna
  // relación con las categorías más flexibles que ya usan el importador de
  // estados de cuenta y el resto de la app.
  const [customCategories, setCustomCategories] = useState([]);
  // Recarga cuentas/reglas de proveedor/categorías personalizadas -- se
  // factorizó fuera del useEffect de montaje para poder llamarla también
  // al terminar (antes: "Unificar categorías duplicadas", ver comentario
  // junto a handleCloseNew más abajo -- ese botón se quitó de esta
  // pantalla el 17 sep 2026, pero la función se dejó igual porque el
  // useEffect de montaje de más abajo también la usa).
  const recargarDatosDeCategorias = () => {
    merchantRuleService.getAll()
      .then((res) => setMerchantRules(res.data || []))
      .catch(() => setMerchantRules([]));
    categoryService.getAll()
      .then((res) => setCustomCategories(res.data || []))
      .catch(() => setCustomCategories([]));
  };
  // 28 ago 2026 (pedido de Augusto: "ya está importado! [y sigue en
  // estimado]"): cuentas y balanceSnapshots se pedían UNA sola vez al
  // montar la página -- si el usuario importaba un estado de cuenta desde
  // el diálogo del header (NotionHeader.js/StatementImporter.js, que vive
  // fuera de esta página y puede abrirse estando ya en Transacciones) sin
  // refrescar el navegador, esta página seguía con los datos viejos: la
  // cuenta sin su dia_cierre recién detectado, y sobre todo sin el
  // AccountBalanceSnapshot recién creado -- por eso el ciclo seguía
  // marcado "estimado" con el número antiguo aunque la importación sí
  // había funcionado. Mismo bug (y mismo arreglo) que ya se había hecho
  // antes en CuentasPage.js/fetchAccounts: se factoriza el fetch para
  // poder volver a llamarlo cuando StatementImporter.js avisa que terminó
  // una importación (evento 'pcm:accounts-updated'), sin depender de un
  // refresh manual de la página.
  const recargarCuentasYSnapshots = useCallback(() => {
    apiClient.get('/dashboard/accounts')
      .then((res) => setCuentas(res.data || []))
      .catch(() => setCuentas([]));
    apiClient.get('/dashboard/accounts/balance-snapshots')
      .then((res) => setBalanceSnapshots(res.data || []))
      .catch(() => setBalanceSnapshots([]));
  }, []);
  useEffect(() => {
    dispatch(fetchTransactions());
    recargarCuentasYSnapshots();
    recargarDatosDeCategorias();
    // 5 ago 2026: se necesitan los recurrentes ya activos para el
    // detector de candidatos (ver handleCreateTx más abajo) -- si un
    // proveedor ya es recurrente, no se le vuelve a preguntar.
    dispatch(fetchIncomes());
    dispatch(fetchExpenses());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  useEffect(() => {
    // StatementImporter.js dispara este evento (window.dispatchEvent)
    // justo después de que una importación termina bien -- ver también
    // CuentasPage.js, que ya escuchaba este mismo evento para su propia
    // tabla de cuentas.
    window.addEventListener('pcm:accounts-updated', recargarCuentasYSnapshots);
    return () => window.removeEventListener('pcm:accounts-updated', recargarCuentasYSnapshots);
  }, [recargarCuentasYSnapshots]);
  const buscarCategoriaPorProveedor = (proveedor) => {
    const p = (proveedor || '').trim().toLowerCase();
    if (!p) return null;
    const regla = merchantRules.find((r) => (r.proveedor || '').trim().toLowerCase() === p);
    return regla ? regla.categoria : null;
  };
  const proveedoresConocidos = useMemo(
    () => Array.from(new Set(merchantRules.map((r) => r.proveedor).filter(Boolean))),
    [merchantRules]
  );
  // Lista completa de categorías disponibles: las 9 fijas de siempre + las
  // que el usuario creó (Category) + cualquier categoría que ya se haya
  // "aprendido" por proveedor (merchant_rules) — así, si un proveedor
  // importado quedó con una categoría en inglés (ej. "food"), esa
  // categoría aparece como opción válida acá también, en vez de quedar
  // huérfana la primera vez que se auto-completa en esta pantalla.
  //
  // 19 ago 2026 (pedido de Augusto: "por qué se repiten las categorías??
  // no debería permitir entrar otra si ya existe, no importa si es un
  // tema de mayúsculas o minúsculas o si hay problemas ortográficos"): la
  // clave de deduplicación era solo `.trim().toLowerCase()`.
  //
  // 20 ago 2026 (Augusto: "Alimentación", "Compras", "Entretenimiento" y
  // "Otro" seguían apareciendo dos veces incluso después de normalizar
  // tildes/mayúsculas, y "Unificar categorías duplicadas" reportaba 0
  // cambios): la causa real no era de acentos. Las importaciones con IA
  // (WhatsApp, Importador de Estados de Cuenta) guardan la categoría en
  // INGLÉS (food/transport/bills/shopping/entertainment/salary/other, ver
  // EXTRACTION_SYSTEM_PROMPT en backend_new/.../statements.py), y
  // traducirCategoria (definida arriba) ya las traduce para MOSTRARLAS en
  // español -- por eso "food" y "Alimentación" se VEÍAN idénticas en el
  // dropdown sin serlo como texto, y ninguna normalización de
  // tildes/mayúsculas las iba a reconocer como la misma categoría. La
  // clave de dedup ahora pasa cada nombre por traducirCategoria(n, 'es')
  // ANTES de normalizarCategoria -- así "food" y "Alimentación" (o
  // "shopping"/"Compras", "entertainment"/"Entretenimiento",
  // "other"/"otro"/"Otro") caen en la misma clave. Se usa 'es' fijo (no
  // el idioma actual de la interfaz) para que la clave no cambie sola si
  // el usuario cambia de idioma. El fix equivalente en el backend está en
  // _normalizar_categoria (merchant_rules.py, vía _TRADUCCION_CATEGORIA_BASE)
  // -- correr de nuevo "Unificar categorías duplicadas" después de este
  // despliegue sí debería fusionar estos casos en la base de datos.
  const allCategorias = useMemo(() => {
    const nombres = [
      ...categorias,
      ...customCategories.map((c) => c.nombre),
      ...merchantRules.map((r) => r.categoria),
    ];
    const vistos = new Set();
    const resultado = [];
    nombres.forEach((n) => {
      const key = normalizarCategoria(traducirCategoria(n, 'es'));
      if (!key || vistos.has(key)) return;
      vistos.add(key);
      resultado.push(n);
    });
    // 5 ago 2026 (pedido de Augusto): el dropdown de categorías salía en
    // el orden en que se creó cada una (las 9 fijas primero, después las
    // personalizadas en orden de creación) -- se ordena alfabéticamente
    // por el texto que realmente ve el usuario (ya traducido, ver
    // traducirCategoria), no por la clave interna en inglés, para que
    // "food" no se ordene como "food" sino como "Alimentación".
    return resultado.sort((a, b) =>
      traducirCategoria(a, language).localeCompare(traducirCategoria(b, language), 'es', { sensitivity: 'base' })
    );
  }, [customCategories, merchantRules, language]);
  // 28 ago 2026 (pedido de Augusto: "Categorias no te da la opcion de
  // cambiar el emoji"): mapa de emojis guardados a mano en CategoriesPage.js
  // (Category.emoji, ver endpoints/categories.py) -- se pasa a TODOS los
  // getCategoryEmoji() de esta página para que el emoji elegido ahí se
  // vea también acá, en vez de solo el automático por palabra clave.
  const emojiOverrides = useMemo(() => buildEmojiOverrides(customCategories), [customCategories]);
  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [nuevaTx, setNuevaTx] = useState(emptyTx);
  const [txEditando, setTxEditando] = useState(null);
  // 21 sep 2026 (Augusto: "trato de editar una transacción y no me
  // aparece Transferencia, solo ingreso egreso").
  //
  // "Transferencia" no es un tipo: una transferencia son DOS movimientos
  // enlazados (sale de una cuenta, entra a otra). Por eso no puede estar
  // en ese desplegable -- elegirlo dejaría media transferencia, plata
  // saliendo de un lado y llegando a ninguno.
  //
  // Lo que sí hace falta es esto: un movimiento que YA existe y que en
  // realidad fue una transferencia. Acá se elige la otra cuenta y el
  // backend arma el otro lado y los enlaza.
  const [convAbierto, setConvAbierto] = useState(false);
  const [convContraparte, setConvContraparte] = useState('');
  const [convMonto, setConvMonto] = useState('');
  const [convError, setConvError] = useState(null);
  const [convCargando, setConvCargando] = useState(false);
  const [convTipo, setConvTipo] = useState('');
  // 21 sep 2026 (Augusto: "estas transacciones que son en realidad
  // transferencias deberían ser editadas como transferencias, no
  // separadamente como ingreso y/o egreso").
  //
  // Una transferencia son dos filas enlazadas. Editar una sola las hacía
  // contradecirse -- salieron 720 de un lado, llegaron 500 al otro -- y
  // como una transferencia no suma ni en ingresos ni en gastos, eso no
  // salía en ningún total: solo deformaba los saldos, en silencio. Así
  // que al abrir el lápiz sobre CUALQUIERA de los dos lados se edita la
  // transferencia entera.
  const [transfEdit, setTransfEdit] = useState(null);
  // 5 ago 2026: diálogo de "¿alguno de estos es recurrente?" (ver
  // handleCreateTx) -- al crear una transacción a mano, si el
  // proveedor+monto ya apareció ~1 mes atrás se pregunta antes de
  // guardar. `pendingTxPayload` guarda el payload de la transacción ya
  // armado mientras se espera la respuesta del diálogo.
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringCandidatos, setRecurringCandidatos] = useState([]);
  const [pendingTxPayload, setPendingTxPayload] = useState(null);
  // 12 ago 2026 (pedido de Augusto): "seleccionar varias transacciones y
  // borrarlas juntas" + "modificar cada campo con doble clic". Sin
  // endpoint de borrado masivo en el backend -- se manda un DELETE por
  // cada id seleccionado (Promise.all), suficiente para los volúmenes de
  // esta app. La edición inline reutiliza el mismo updateTransaction de
  // siempre, pero mandando SOLO el campo tocado (el backend ya soporta
  // updates parciales -- ver exclude_unset=True en endpoints/
  // transactions.py -- así no hace falta reconstruir toda la transacción
  // para cambiar un solo dato).
  // 7 sep 2026 (Augusto: "está super lento. Cuando intento scroll arriba
  // el aplicativo no responde"): esta tabla renderizaba TODAS las
  // transacciones filtradas de una sola vez, sin paginación -- cada fila
  // (renderFilaTx, más abajo) es bastante pesada (10 celdas, 3 Tooltips +
  // 3 IconButtons, un Chip, más los campos editables inline), así que con
  // cientos de transacciones acumuladas (cada importación de estado de
  // cuenta agrega decenas) el navegador terminaba con miles de nodos DOM
  // montados a la vez -- eso es lo que trababa el scroll, sobre todo en
  // celular. Se agrega paginación (ver TablePagination más abajo, junto a
  // TableContainer): la tabla ahora solo monta las filas de la página
  // actual. Aplica solo a la vista SIN agrupar (gruposAnidados === null,
  // ver más abajo) -- la vista agrupada (por categoría/proveedor/etc.)
  // queda para una mejora aparte, es un cambio más grande paginar un
  // árbol de grupos anidados.
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(50);
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // 30 ago 2026 (pedido de Augusto: "debería también haber alguna opción
  // para marcar en la lista de transacciones que permita cambiar
  // transacciones a recurrentes en masa"): mismo patrón que el borrado
  // masivo de arriba -- un botón que solo aparece con algo seleccionado,
  // un diálogo de confirmación (acá con un selector de frecuencia, misma
  // aplicada a TODAS las seleccionadas), y un POST /recurring/incomes o
  // /expenses por cada transacción (no hay endpoint de creación masiva).
  // Cada transacción seleccionada queda intacta -- esto solo CREA series
  // recurrentes nuevas a partir de ellas (ver buildRecurringPayloadDesdeTransaccion).
  const [openBulkRecurring, setOpenBulkRecurring] = useState(false);
  const [bulkRecurringFrecuencia, setBulkRecurringFrecuencia] = useState('mensual');
  const [bulkRecurringSaving, setBulkRecurringSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');
  // 17 sep 2026: el botón "Agregar Transacción" (que llamaba a
  // handleOpenNew = () => setOpenNew(true)) se quitó de la barra de
  // Transacciones -- ver comentario junto al Box de botones más abajo.
  // El diálogo (openNew) sigue existiendo porque "Duplicar transacción"
  // (ícono por fila, ver handleDuplicateTx) lo reutiliza para precargar
  // una copia editable.
  const handleCloseNew = () => {
    setOpenNew(false);
    setNuevaTx(emptyTx);
  };
  // 28 ago 2026 (pedido de Augusto: "existe la opción para que el usuario
  // pueda duplicar un egreso o ingreso y luego hacer las
  // modificaciones?"): reutiliza el mismo diálogo de "Agregar
  // Transacción" (openNew/nuevaTx) pero YA precargado con los datos de
  // `tx` -- el usuario solo tiene que ajustar lo que cambió (monto,
  // categoría, proveedor...) y guardar, en vez de tipear todo de cero.
  // La fecha queda en blanco a propósito (mismo comportamiento que crear
  // una transacción nueva desde cero -- el backend le pone la fecha de
  // hoy) porque una transacción duplicada casi siempre es un movimiento
  // NUEVO que recién está pasando, no uno con la fecha vieja del
  // original; si hace falta la fecha vieja, se puede corregir después con
  // doble clic en la celda de Fecha, igual que cualquier otra fila.
  const handleDuplicateTx = (tx) => {
    setNuevaTx({
      ...emptyTx,
      tipo: tx.tipo,
      monto: tx.monto ?? '',
      categoria: tx.categoria || 'Otro',
      proveedor: tx.proveedor || '',
      descripcion: tx.descripcion || '',
      account_id: tx.account_id ?? '',
    });
    setOpenNew(true);
  };
  // 30 ago 2026 (pedido de Augusto: "no me da la opción en el formulario
  // de meter gastos o ingresos recurrentes" -- el switch "¿Es
  // recurrente?" solo existía en "Agregar Transacción", nunca en
  // "Editar"): se agregan los mismos campos transitorios que usa
  // handleCreateTx (crear_recurrente/frecuencia/dia_mes), con una
  // diferencia importante -- acá NO reemplaza a la transacción (que ya es
  // un movimiento real, pasado o presente) sino que ADEMÁS crea la serie
  // recurrente hacia adelante (ver handleUpdateTxSubmit). dia_mes se
  // precarga con el día de la fecha REAL de esta transacción (no "hoy"),
  // para que "mensual" quede anclada al día correcto por defecto.
  const handleOpenEdit = (tx) => {
    const diaTx = tx.fecha ? new Date(tx.fecha).getDate() : new Date().getDate();
    setTxEditando({
      ...tx,
      account_id: tx.account_id || '',
      crear_recurrente: false,
      frecuencia: 'mensual',
      dia_mes: String(diaTx),
    });
    setConvAbierto(false);
    setConvContraparte('');
    setConvMonto('');
    setConvTipo('');
    setConvError(null);
    setTransfEdit(null);
    setOpenEdit(true);
    // Si es una transferencia, se pide el par completo y el diálogo pasa
    // a ser el editor de transferencia (ver más abajo).
    if (tx.transferencia_par_id) {
      apiClient.get(`/transactions/${tx.id}/transferencia`)
        .then(({ data }) => setTransfEdit({
          cuentaOrigenId: data.origen.account_id || '',
          cuentaDestinoId: data.destino.account_id || '',
          monto: String(data.origen.monto ?? ''),
          montoDestino: String(data.destino.monto ?? ''),
          // La moneda sale del MOVIMIENTO, no de la cuenta: una tarjeta
          // es una cuenta con una moneda pero puede deber en dos, y un
          // pago en dólares a una tarjeta registrada en soles es normal.
          monedaOrigen: (data.origen.moneda || 'PEN').toUpperCase(),
          monedaDestino: (data.destino.moneda || 'PEN').toUpperCase(),
          destinoEsTarjeta: !!data.destino.es_tarjeta,
          monedasTarjeta: data.destino.monedas_tarjeta || [],
          fecha: data.fecha ? String(data.fecha).slice(0, 10) : '',
          // Subtipo de transferencia. NO es la categoría: la de una
          // transferencia siempre es "Transferencia", y ese valor es lo
          // que hace que no cuente como ingreso ni gasto.
          transferenciaTipo: data.transferencia_tipo || '',
        }))
        // Si el backend todavía no tiene el endpoint (ventana de deploy),
        // se queda el formulario de siempre en vez de un diálogo vacío.
        .catch(() => setTransfEdit(null));
    }
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setTxEditando(null);
    setConvAbierto(false);
    setConvError(null);
    setTransfEdit(null);
  };

  const handleGuardarTransferencia = async () => {
    if (!txEditando || !transfEdit) return;
    setConvCargando(true);
    setConvError(null);
    try {
      await apiClient.put(`/transactions/${txEditando.id}/transferencia`, {
        cuenta_origen_id: transfEdit.cuentaOrigenId || null,
        cuenta_destino_id: transfEdit.cuentaDestinoId || null,
        monto: transfEdit.monto !== '' ? Number(transfEdit.monto) : null,
        monto_destino: transfEdit.montoDestino !== '' ? Number(transfEdit.montoDestino) : null,
        moneda_destino: transfEdit.monedaDestino || null,
        transferencia_tipo: transfEdit.transferenciaTipo || null,
        // T12:00:00 para que UTC-5 no lo tire al día anterior.
        fecha: transfEdit.fecha ? `${transfEdit.fecha}T12:00:00` : null,
      });
      dispatch(fetchTransactions());
      recargarCuentasYSnapshots();
      handleCloseEdit();
    } catch (err) {
      setConvError(
        err.response?.data?.detail
        || (language === 'es' ? 'No se pudo guardar la transferencia.' : 'Could not save the transfer.')
      );
    } finally {
      setConvCargando(false);
    }
  };

  // Convertir un movimiento existente en transferencia. El backend crea
  // el lado que falta, ajusta el saldo SOLO de la cuenta nueva (la
  // original ya se movió cuando se registró) y enlaza los dos.
  const handleConvertirEnTransferencia = async () => {
    if (!txEditando || !convContraparte) return;
    setConvCargando(true);
    setConvError(null);
    try {
      await apiClient.post(`/transactions/${txEditando.id}/convertir-en-transferencia`, {
        cuenta_contraparte_id: convContraparte,
        ...(convMonto !== '' ? { monto_contraparte: Number(convMonto) } : {}),
        ...(convTipo ? { transferencia_tipo: convTipo } : {}),
      });
      dispatch(fetchTransactions());
      recargarCuentasYSnapshots();
      handleCloseEdit();
    } catch (err) {
      setConvError(
        err.response?.data?.detail
        || (language === 'es' ? 'No se pudo convertir en transferencia.' : 'Could not convert to a transfer.')
      );
    } finally {
      setConvCargando(false);
    }
  };
  // 17 sep 2026 (pedido de Augusto: "eliminar de la sección Transacciones
  // ... duplicar, y unificar categorías"): se quitaron de acá los botones
  // "Duplicar mes anterior" (GET /transactions/duplicate-preview + POST
  // /transactions/duplicate-last-month) y "Unificar categorías
  // duplicadas" (POST /transactions/normalize-categorias), junto con todo
  // su estado y handlers (handleOpen/Close/ConfirmDuplicate,
  // handleOpen/Close/ConfirmUnify) y sus diálogos de confirmación. Ambos
  // endpoints del backend siguen intactos por si hace falta un botón
  // equivalente en otro lado más adelante -- solo se quitó el acceso
  // desde esta pantalla.
  // 28 ago 2026 (pedido de Augusto: "se duplicaron las entradas...
  // bórrame las duplicadas"): reintentar una importación que ya se había
  // completado antes (ej. sin darse cuenta de que la primera vez sí
  // había llegado a importar, aunque mostrara un aviso de cuota) crea las
  // mismas transacciones otra vez -- el aviso "posible duplicado" de la
  // pantalla de importación solo avisa, no bloquea. Mismo patrón que
  // "Unificar categorías duplicadas" de arriba: idempotente, con diálogo
  // de confirmación explícito antes de borrar nada (ver
  // POST /transactions/deduplicate en transactions.py).
  const [dedupDialogOpen, setDedupDialogOpen] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupError, setDedupError] = useState(null);
  const [dedupResult, setDedupResult] = useState(null);
  const handleOpenDedupDialog = () => {
    setDedupDialogOpen(true);
    setDedupError(null);
    setDedupResult(null);
  };
  const handleCloseDedupDialog = () => {
    setDedupDialogOpen(false);
    setDedupError(null);
  };
  const handleConfirmDedup = async () => {
    setDedupLoading(true);
    setDedupError(null);
    try {
      const { data } = await apiClient.post('/transactions/deduplicate');
      setDedupResult(data);
      dispatch(fetchTransactions());
    } catch (err) {
      setDedupError(err.response?.data?.detail || 'No se pudieron eliminar las duplicadas.');
    } finally {
      setDedupLoading(false);
    }
  };
  const handleDeleteConfirmOpen = (id) => {
    setDeleteId(id);
    setOpenDelete(true);
  };
  const handleDeleteConfirmClose = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'account_id' && value === '__crear_cuenta__') {
      abrirNuevaCuentaRapida('nuevaTx');
      return;
    }
    setNuevaTx((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === 'account_id' && value === '__crear_cuenta__') {
      abrirNuevaCuentaRapida('txEditando');
      return;
    }
    setTxEditando((prev) => ({ ...prev, [name]: value }));
  };

  // Abre el diálogo de alta rápida de cuenta recordando a qué formulario
  // (nuevaTx o txEditando) asignarle la cuenta apenas se cree.
  const abrirNuevaCuentaRapida = (target) => {
    setCuentaRapidaTarget(target);
    setErrorCuentaRapida(null);
    setNuevaCuentaRapida({ nombre: '', tipo: 'ahorros', moneda: 'PEN', balance: '' });
    setOpenNuevaCuentaRapida(true);
  };

  const handleNuevaCuentaRapidaChange = (e) => {
    const { name, value } = e.target;
    setNuevaCuentaRapida((prev) => ({ ...prev, [name]: value }));
  };

  // El backend solo exige `nombre` (ver schemas/account.py AccountBase) --
  // tipo/moneda/saldo ya traen default, así que este formulario reducido
  // (sin Entidad Bancaria ni ícono, a diferencia de Cuentas > Nueva
  // Cuenta) es suficiente para no interrumpir el flujo de registrar una
  // transacción.
  const handleGuardarCuentaRapida = async () => {
    if (!nuevaCuentaRapida.nombre.trim()) return;
    setGuardandoCuentaRapida(true);
    setErrorCuentaRapida(null);
    try {
      const { data } = await apiClient.post('/dashboard/accounts', {
        nombre: nuevaCuentaRapida.nombre.trim(),
        tipo: nuevaCuentaRapida.tipo,
        saldo: parseFloat(nuevaCuentaRapida.balance) || 0,
        moneda: nuevaCuentaRapida.moneda,
      });
      setCuentas((prev) => [...prev, data]);
      if (cuentaRapidaTarget === 'txEditando') {
        setTxEditando((prev) => (prev ? { ...prev, account_id: data.id } : prev));
      } else {
        setNuevaTx((prev) => ({ ...prev, account_id: data.id }));
      }
      setOpenNuevaCuentaRapida(false);
    } catch (err) {
      setErrorCuentaRapida(
        err.response?.data?.detail || (language === 'es' ? 'No se pudo crear la cuenta.' : 'Could not create the account.')
      );
    } finally {
      setGuardandoCuentaRapida(false);
    }
  };
  // 4 sep 2026 (Augusto: "la cuenta es de dolares, pero el símbolo sale
  // en soles"): este diálogo tiene selector de Cuenta pero nunca tuvo
  // selector de Moneda propio (ver comentario más abajo en
  // buildRecurringPayload) -- buildPayload ni siquiera mandaba el campo,
  // así que toda transacción nueva quedaba con moneda=null (TransactionsPage
  // la muestra como "S/" por defecto, ver formatTxAmount) sin importar
  // que la Cuenta elegida fuera en dólares. Se deriva de la Cuenta
  // seleccionada; si `data` ya trae una moneda propia (editando una
  // transacción existente que vino de un extracto importado en USD), esa
  // se respeta en vez de pisarla.
  const monedaDeCuenta = (accountId) => {
    if (accountId === '' || accountId == null) return null;
    return cuentas.find((c) => c.id === Number(accountId))?.moneda || null;
  };
  // 28 sep 2026: monedas que se pueden elegir al registrar un movimiento.
  // Vienen del backend (MONEDAS_SOPORTADAS) para que agregar una sea una
  // línea allá y no haya que tocar esta pantalla.
  const [monedasDisponibles, setMonedasDisponibles] = useState([
    { codigo: 'PEN', nombre: 'Sol peruano' },
    { codigo: 'USD', nombre: 'Dólar estadounidense' },
  ]);
  useEffect(() => {
    apiClient.get('/transactions/monedas')
      .then((r) => { if (r.data?.length) setMonedasDisponibles(r.data); })
      .catch(() => {});
  }, []);
  // Equivalente en dólares de lo que se está escribiendo, cuando la
  // moneda no es soles ni dólares.
  const [conversionPreview, setConversionPreview] = useState(null);
  const [errorConversion, setErrorConversion] = useState(null);
  const monedaElegidaTx = nuevaTx.moneda || monedaDeCuenta(nuevaTx.account_id) || 'PEN';
  useEffect(() => {
    const monto = parseFloat(nuevaTx.monto);
    if (!monto || ['PEN', 'USD'].includes(monedaElegidaTx)) {
      setConversionPreview(null);
      setErrorConversion(null);
      return undefined;
    }
    // Espera a que deje de escribir: sin esto se consulta la tasa en cada
    // tecla del monto.
    const id = setTimeout(() => {
      apiClient.get('/transactions/conversion', {
        params: { monto, moneda: monedaElegidaTx, fecha: nuevaTx.fecha || undefined },
      })
        .then((r) => { setConversionPreview(r.data); setErrorConversion(null); })
        .catch((err) => {
          setConversionPreview(null);
          setErrorConversion(
            err.response?.data?.detail ||
              (language === 'es'
                ? 'No pude obtener el tipo de cambio. Sin él no puedo guardar este monto.'
                : 'Could not get the exchange rate. Without it this amount cannot be saved.')
          );
        });
    }, 500);
    return () => clearTimeout(id);
  }, [nuevaTx.monto, monedaElegidaTx, nuevaTx.fecha, language]);

  const buildPayload = (data) => ({
    tipo: data.tipo,
    monto: parseFloat(data.monto) || 0,
    categoria: data.categoria || null,
    proveedor: data.proveedor?.trim() || null,
    descripcion: data.descripcion?.trim() || null,
    account_id: data.account_id === '' ? null : Number(data.account_id),
    // 28 sep 2026: se manda la moneda TAL CUAL la eligió el usuario. Si
    // es una tercera moneda, el backend convierte a dólares y guarda el
    // original (ver create_transaction). Convertir acá sería duplicar la
    // regla en dos sitios.
    moneda: data.moneda || monedaDeCuenta(data.account_id),
  });
  // Construye el payload para /recurring/incomes o /recurring/expenses
  // cuando la transacción se marca como recurrente. Para "mensual" se
  // arma fecha_inicio a partir del día del mes elegido (próxima
  // ocurrencia: este mes si el día no pasó todavía, si no el que viene).
  // Para "diario" y "quincenal" (días 15 y 30 fijos) no hace falta elegir
  // día — fecha_inicio es simplemente hoy, y calendar.py se encarga del
  // resto (ver _generate_occurrences).
  const buildRecurringPayload = (data) => {
    let fecha_inicio = hoyISO();
    if (data.frecuencia === 'mensual') {
      const hoy = new Date();
      const diaElegido = parseInt(data.dia_mes, 10) || hoy.getDate();
      let mes = hoy.getMonth();
      let anio = hoy.getFullYear();
      if (diaElegido < hoy.getDate()) {
        mes += 1;
        if (mes > 11) {
          mes = 0;
          anio += 1;
        }
      }
      const ultimoDiaMes = new Date(anio, mes + 1, 0).getDate();
      const diaFinal = Math.min(diaElegido, ultimoDiaMes);
      fecha_inicio = new Date(anio, mes, diaFinal).toISOString().slice(0, 10);
    }
    return {
      nombre: data.descripcion?.trim() || data.proveedor?.trim() || (data.tipo === 'ingreso' ? 'Ingreso recurrente' : 'Egreso recurrente'),
      monto: parseFloat(data.monto) || 0,
      frecuencia: data.frecuencia,
      categoria: data.categoria || null,
      account_id: data.account_id === '' ? null : Number(data.account_id),
      // 30 ago 2026: igual que buildRecurringPayloadDesdeTransaccion en
      // recurringDetection.js -- se conserva la moneda de la transacción
      // si ya trae una (una transacción existente que se marca como
      // recurrente desde "Editar" puede traer "USD" si vino de un estado
      // de cuenta importado). 4 sep 2026 (Augusto: "la cuenta es de
      // dolares, pero el símbolo sale en soles"): para una transacción
      // NUEVA (sin moneda propia todavía) ahora se deriva de la Cuenta
      // elegida en vez de caer siempre en null/soles -- ver
      // monedaDeCuenta arriba.
      moneda: data.moneda || monedaDeCuenta(data.account_id),
      fecha_inicio,
      fecha_fin: null,
    };
  };
  // Al elegir/escribir un proveedor ya conocido, se auto-completa la
  // categoría con la que se guardó la última vez (ver merchant_rules.py) —
  // el usuario igual puede cambiarla manualmente si esta vez corresponde a
  // otra categoría.
  const handleProveedorChange = (setter) => (_e, value) => {
    const proveedor = value || '';
    setter((prev) => {
      const categoriaSugerida = buscarCategoriaPorProveedor(proveedor);
      return {
        ...prev,
        proveedor,
        categoria: categoriaSugerida || prev.categoria,
      };
    });
  };
  // 5 ago 2026 (Augusto: "sería bueno tener... la opción [de detectar
  // recurrentes]... quizás cuando sean 2, se puede hacer un pop up para
  // preguntar si es recurrente dado que se repitió el mes pasado"): antes
  // de guardar una transacción nueva (cuando el usuario NO activó a mano
  // el switch "¿Es recurrente?"), se compara contra el historial ya
  // cargado -- si el mismo proveedor+monto ya apareció hace ~1 mes, se
  // pregunta con RecurringCandidatesDialog antes de guardar. Ver
  // utils/recurringDetection.js para el criterio exacto.
  const handleCreateTx = async () => {
    if (!nuevaTx.monto) return;
    if (nuevaTx.es_recurrente) {
      // Switch manual ya activado -- sin cambios, se comporta como antes
      // (crea SOLO la serie recurrente, no una transacción puntual).
      const payload = buildRecurringPayload(nuevaTx);
      if (nuevaTx.tipo === 'ingreso') {
        await dispatch(addIncome(payload));
      } else {
        await dispatch(addExpense(payload));
      }
      handleCloseNew();
      return;
    }
    const payload = buildPayload(nuevaTx);
    const nuevaParaDetectar = {
      proveedor: payload.proveedor,
      monto: payload.monto,
      tipo: payload.tipo,
      fecha: hoyISO(),
      categoria: payload.categoria,
      accountId: payload.account_id,
    };
    const historialParaDetectar = transactions.map((t) => ({
      proveedor: t.proveedor,
      monto: t.monto,
      tipo: t.tipo,
      fecha: t.fecha,
    }));
    const candidatos = detectarCandidatosRecurrentes(
      [nuevaParaDetectar],
      historialParaDetectar,
      [...recurringIncomes, ...recurringExpenses]
    );
    if (candidatos.length > 0) {
      setRecurringCandidatos(candidatos);
      setPendingTxPayload(payload);
      setRecurringDialogOpen(true);
      return; // se guarda al confirmar/cancelar el diálogo, ver abajo
    }
    await dispatch(addTransaction(payload));
    handleCloseNew();
  };
  // El usuario confirmó (con o sin items seleccionados) el diálogo de
  // candidatos a recurrente -- se guarda la transacción normal primero
  // (siempre, es un movimiento real) y, por cada candidato marcado, se
  // crea además la serie recurrente correspondiente.
  const handleConfirmRecurringCandidates = async (elegidos) => {
    setRecurringDialogOpen(false);
    if (!pendingTxPayload) return;
    await dispatch(addTransaction(pendingTxPayload));
    for (const c of elegidos) {
      const recurPayload = buildRecurringPayloadDesdeTransaccion(c.nueva);
      if (c.nueva.tipo === 'ingreso') {
        await dispatch(addIncome(recurPayload));
      } else {
        await dispatch(addExpense(recurPayload));
      }
    }
    setPendingTxPayload(null);
    setRecurringCandidatos([]);
    handleCloseNew();
  };
  const handleCancelRecurringCandidates = async () => {
    setRecurringDialogOpen(false);
    if (!pendingTxPayload) return;
    await dispatch(addTransaction(pendingTxPayload));
    setPendingTxPayload(null);
    setRecurringCandidatos([]);
    handleCloseNew();
  };
  // 30 ago 2026: si se marcó "Crear serie recurrente" en el diálogo de
  // edición, además de guardar los cambios de ESTA transacción (que sigue
  // siendo un movimiento real puntual) se crea una serie recurrente nueva
  // hacia adelante -- misma forma de payload (buildRecurringPayload) que
  // ya usaba el switch de "Agregar Transacción", reutilizando frecuencia/
  // dia_mes precargados en handleOpenEdit.
  const handleUpdateTxSubmit = async () => {
    if (!txEditando?.monto) return;
    await dispatch(updateTransaction({ id: txEditando.id, data: buildPayload(txEditando) }));
    if (txEditando.crear_recurrente) {
      const recurPayload = buildRecurringPayload(txEditando);
      if (txEditando.tipo === 'ingreso') {
        await dispatch(addIncome(recurPayload));
      } else {
        await dispatch(addExpense(recurPayload));
      }
    }
    handleCloseEdit();
  };
  const handleDeleteTx = async () => {
    if (deleteId) {
      await dispatch(deleteTransaction(deleteId));
      handleDeleteConfirmClose();
    }
  };
  const toggleSeleccion = (id) => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const handleBulkDeleteOpen = () => setOpenBulkDelete(true);
  const handleBulkDeleteClose = () => setOpenBulkDelete(false);
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      // 30 oct 2026 (Augusto: "¿no hay una opción para marcarlas y
      // eliminarlas?", con 5 transferencias = 10 filas). Antes esto borraba
      // todo EN PARALELO: si se marcaban los dos lados de una transferencia,
      // los dos pedidos intentaban borrar el mismo par a la vez, y varios
      // borrados simultáneos sobre la misma cuenta podían pisarse el saldo.
      // Ahora: un solo pedido por transferencia (el backend se lleva los dos
      // lados) y de a uno.
      const yaCubiertas = new Set();
      const porBorrar = [];
      transactions
        .filter((t) => seleccionadas.has(t.id))
        .forEach((t) => {
          if (yaCubiertas.has(t.id)) return;
          porBorrar.push(t.id);
          yaCubiertas.add(t.id);
          if (t.transferencia_par_id) yaCubiertas.add(t.transferencia_par_id);
        });
      for (const id of porBorrar) {
        // eslint-disable-next-line no-await-in-loop
        await dispatch(deleteTransaction(id));
      }
      // El otro lado de cada transferencia lo borró el backend: se recarga
      // la lista para que no quede a la vista una fila que ya no existe.
      dispatch(fetchTransactions());
    } finally {
      setBulkDeleting(false);
      setSeleccionadas(new Set());
      setOpenBulkDelete(false);
    }
  };
  const handleBulkRecurringOpen = () => {
    setBulkRecurringFrecuencia('mensual');
    setOpenBulkRecurring(true);
  };
  const handleBulkRecurringClose = () => setOpenBulkRecurring(false);
  // Crea una serie recurrente por cada transacción seleccionada (no las
  // borra ni las modifica -- ver comentario junto a los estados de
  // arriba). Se manda en serie (no Promise.all) para no disparar decenas
  // de POST simultáneos si se seleccionaron muchas filas de golpe.
  const handleBulkRecurring = async () => {
    setBulkRecurringSaving(true);
    try {
      const txsSeleccionadas = transactions.filter((t) => seleccionadas.has(t.id));
      for (const tx of txsSeleccionadas) {
        const payload = buildRecurringPayloadDesdeTransaccion(
          { ...tx, accountId: tx.account_id },
          bulkRecurringFrecuencia
        );
        // eslint-disable-next-line no-await-in-loop
        if (tx.tipo === 'ingreso') {
          // eslint-disable-next-line no-await-in-loop
          await dispatch(addIncome(payload));
        } else {
          // eslint-disable-next-line no-await-in-loop
          await dispatch(addExpense(payload));
        }
      }
    } finally {
      setBulkRecurringSaving(false);
      setSeleccionadas(new Set());
      setOpenBulkRecurring(false);
    }
  };
  // Campos que se pueden tocar con doble clic directo en la tabla --
  // "tipo" cambia Ingreso/Egreso, el resto son los mismos que ya tenía el
  // formulario de edición completo.
  const CAMPOS_EDITABLES_INLINE = ['fecha', 'tipo', 'categoria', 'proveedor', 'descripcion', 'account_id', 'monto'];
  const startEdit = (tx, field) => {
    if (!CAMPOS_EDITABLES_INLINE.includes(field)) return;
    setEditingCell({ id: tx.id, field });
    if (field === 'fecha') setEditValue(tx.fecha ? tx.fecha.slice(0, 10) : hoyISO());
    else if (field === 'account_id') setEditValue(tx.account_id ?? '');
    else setEditValue(tx[field] ?? '');
  };
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };
  const commitEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    let valor = editValue;
    if (field === 'monto') valor = parseFloat(editValue) || 0;
    if (field === 'account_id') valor = editValue === '' ? null : Number(editValue);
    if (field === 'fecha') {
      if (!editValue) { cancelEdit(); return; }
      valor = editValue; // "YYYY-MM-DD" -- FastAPI/Pydantic lo interpreta bien como datetime.
    }
    await dispatch(updateTransaction({ id, data: { [field]: valor } }));
    cancelEdit();
  };
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') cancelEdit();
  };
  const cuentaNombre = (id) => cuentas.find((c) => c.id === id)?.nombre || '—';
  // 28 ago 2026 (pedido de Augusto): dado el día de cierre configurado en
  // la cuenta (cuenta.dia_cierre, 1-31), devuelve la fecha en que cierra
  // el ciclo de facturación al que pertenece `fecha` -- si `fecha` es
  // posterior al día de cierre de ese mes, el ciclo cierra al mes
  // siguiente (mismo criterio que usan los bancos: un consumo del 30 de
  // julio con cierre el 25 cae en el ciclo que cierra el 25 de agosto, no
  // en el que ya cerró el 25 de julio). Math.min contra el último día del
  // mes evita un día de cierre inválido (ej. 31 en un mes de 30 o en
  // febrero).
  const fechaCierreDelCiclo = (fecha, diaCierre) => {
    const d = new Date(fecha);
    let mes = d.getMonth();
    let anio = d.getFullYear();
    const ultimoDiaMesActual = new Date(anio, mes + 1, 0).getDate();
    const corteEsteMes = Math.min(diaCierre, ultimoDiaMesActual);
    if (d.getDate() > corteEsteMes) {
      mes += 1;
      if (mes > 11) { mes = 0; anio += 1; }
    }
    const ultimoDiaMesCierre = new Date(anio, mes + 1, 0).getDate();
    return new Date(anio, mes, Math.min(diaCierre, ultimoDiaMesCierre));
  };
  // Fecha (Date, en horario local) -> "YYYY-MM-DD", para comparar contra
  // balanceSnapshots.fecha_corte (que llega como string en ese formato
  // desde el backend) sin el corrimiento de zona horaria de toISOString().
  const _pad2 = (n) => String(n).padStart(2, '0');
  const fechaLocalISO = (d) => `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
  // 30 oct 2026 (Augusto, filtrando por Fecha de corte: "¿por qué no
  // cuadran los gastos con lo que existe en los estados de cuenta por
  // moneda?").
  //
  // El ciclo de un movimiento salía solo de su fecha y de un día de cierre
  // FIJO. Los bancos no cierran siempre el mismo día (la BCP Visa cerró el
  // 27/09 y el 25/10, y en Deudas figura "el 26") y cuentan por fecha de
  // proceso, así que lo cercano al cierre caía en el ciclo equivocado y el
  // total no coincidía con el extracto. Ahora, en este orden:
  //   1. Si el movimiento vino de un extracto, su ciclo ES el de ese
  //      extracto (fecha_corte_extracto, lo guarda el importador).
  //   2. Si no, el primer cierre REAL conocido de esa tarjeta (los
  //      extractos importados) en o después de su fecha.
  //   3. Recién si no hay ninguno cerca, el día de cierre fijo.
  const _isoADate = (iso) => {
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const cierreDeTx = (tx) => {
    if (!tx.fecha) return null;
    const cuenta = cuentas.find((c) => c.id === tx.account_id);
    if (!cuenta) return null;
    if (tx.fecha_corte_extracto) return _isoADate(tx.fecha_corte_extracto);
    const f = fechaLocalISO(new Date(tx.fecha));
    const cierres = Array.from(
      new Set(balanceSnapshots.filter((s) => s.account_id === cuenta.id).map((s) => String(s.fecha_corte).slice(0, 10)))
    ).sort();
    const siguiente = cierres.find((c) => c >= f);
    if (siguiente && (_isoADate(siguiente) - new Date(tx.fecha)) / 86400000 <= 35) {
      return _isoADate(siguiente);
    }
    if (!cuenta.dia_cierre) return null;
    let cierre = fechaCierreDelCiclo(tx.fecha, cuenta.dia_cierre);
    const ultimo = cierres[cierres.length - 1];
    // Después del último extracto: si la regla fija cae en o antes de ese
    // cierre real, el movimiento es del ciclo siguiente.
    if (ultimo && f > ultimo && fechaLocalISO(cierre) <= ultimo) {
      const diaDespues = new Date(cierre.getFullYear(), cierre.getMonth(), cierre.getDate() + 1);
      cierre = fechaCierreDelCiclo(diaDespues, cuenta.dia_cierre);
    }
    return cierre;
  };
  // 29 ago 2026 (pedido de Augusto: "toca añadir fechas de corte en
  // filtros y mes"): mismo criterio que usa claveDeNivel más abajo para
  // AGRUPAR por 'mes'/'corte', pero como funciones propias acá arriba
  // porque el filtro (transaccionesFiltradas, unas líneas más abajo) se
  // define antes que claveDeNivel en este archivo. Devuelven null cuando
  // no aplican (sin fecha, o sin cuenta con día de cierre configurado
  // para 'corte') -- esas transacciones simplemente quedan fuera del
  // filtro/dropdown en vez de agruparse en un cajón "sin fecha" como sí
  // hace la agrupación.
  const claveMesTx = (tx) => {
    if (!tx.fecha) return null;
    const d = new Date(tx.fecha);
    const label = d.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };
  // 17 sep 2026 (pedido de Augusto: "necesitamos filtro por año"): mismo
  // criterio que claveMesTx pero solo el año -- útil para ver de un
  // vistazo un año completo sin tener que ir mes por mes, o para años
  // viejos donde ya no importa el detalle mensual.
  const claveAnioTx = (tx) => {
    if (!tx.fecha) return null;
    return String(new Date(tx.fecha).getFullYear());
  };
  const claveCorteTx = (tx) => {
    if (!tx.fecha) return null;
    const cuenta = cuentas.find((c) => c.id === tx.account_id);
    const fechaCierre = cierreDeTx(tx);
    if (!cuenta || !fechaCierre) return null;
    const label = fechaCierre.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${cuenta.nombre} — Cierre ${label}`;
  };
  // Opciones de los nuevos filtros "Mes" y "Fecha de corte" -- se listan
  // los valores DISTINTOS presentes en TODAS las transacciones (no solo
  // en lo ya filtrado por los otros filtros, mismo criterio que usan los
  // dropdowns de Cuenta/Categoría), más recientes primero.
  const opcionesFiltroMes = useMemo(() => {
    const vistos = new Map();
    transactions.forEach((tx) => {
      if (!tx.fecha) return;
      const d = new Date(tx.fecha);
      const sortKey = `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}`;
      if (!vistos.has(sortKey)) vistos.set(sortKey, claveMesTx(tx));
    });
    return Array.from(vistos.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, label]) => label);
  }, [transactions]);
  // 17 sep 2026: mismo patrón que opcionesFiltroMes, un valor por año
  // distinto presente en las transacciones, más reciente primero.
  const opcionesFiltroAnio = useMemo(() => {
    const vistos = new Set();
    transactions.forEach((tx) => {
      const anio = claveAnioTx(tx);
      if (anio) vistos.add(anio);
    });
    return Array.from(vistos).sort((a, b) => b.localeCompare(a));
  }, [transactions]);
  const opcionesFiltroCorte = useMemo(() => {
    const vistos = new Map();
    transactions.forEach((tx) => {
      const cuenta = cuentas.find((c) => c.id === tx.account_id);
      const fechaCierre = cierreDeTx(tx);
      if (!cuenta || !fechaCierre) return;
      const sortKey = `${cuenta.id}|${fechaLocalISO(fechaCierre)}`;
      if (!vistos.has(sortKey)) vistos.set(sortKey, claveCorteTx(tx));
    });
    return Array.from(vistos.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, label]) => label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, cuentas, balanceSnapshots]);
  // 28 ago 2026 (pedido de Augusto: "por qué no se resta el depósito que
  // hice... y muestra el saldo real" -- un pago grande fechado DENTRO de
  // un ciclo casi siempre cancela deuda de ciclos ANTERIORES, no la
  // actividad nueva de ese mismo mes, así que el neto de solo-esa-ventana
  // podía dar positivo aunque la tarjeta siguiera endeudada): calcula el
  // saldo ACUMULADO real de una cuenta a una fecha de corte, anclado al
  // snapshot más reciente conocido (balanceSnapshots, viene de estados de
  // cuenta importados -- ver backend_new/.../statements.py,
  // _crear_snapshot_saldo) más los movimientos posteriores a esa ancla.
  // Devuelve null si nunca se importó un estado de cuenta real de esa
  // cuenta+moneda -- el llamador (construirGrupos) cae de vuelta al
  // cálculo de solo-esta-ventana en ese caso, ya que no hay con qué
  // anclar un acumulado confiable.
  const calcularSaldoAcumulado = (accountId, moneda, fechaObjetivo, deltaFn) => {
    const fechaObjetivoISO = fechaLocalISO(fechaObjetivo);
    const snaps = balanceSnapshots
      .filter((s) => s.account_id === accountId && s.moneda === moneda)
      .sort((a, b) => (a.fecha_corte < b.fecha_corte ? -1 : a.fecha_corte > b.fecha_corte ? 1 : 0));
    if (snaps.length === 0) return null;
    const exacto = snaps.find((s) => s.fecha_corte === fechaObjetivoISO);
    if (exacto) return { saldo: exacto.saldo, esEstimado: false };
    const anclas = snaps.filter((s) => s.fecha_corte < fechaObjetivoISO);
    if (anclas.length === 0) return null;
    const ancla = anclas[anclas.length - 1];
    const deltaDesdeAncla = transactions
      .filter((tx) => tx.account_id === accountId && tx.fecha)
      .filter((tx) => {
        const f = fechaLocalISO(new Date(tx.fecha));
        return f > ancla.fecha_corte && f <= fechaObjetivoISO;
      })
      .reduce((acc, tx) => acc + deltaFn(tx), 0);
    return { saldo: ancla.saldo + deltaDesdeAncla, esEstimado: true };
  };
  // 5 ago 2026 (Augusto: "los ingresos registrados en Tarjetas de crédito
  // y otras deudas no deben considerarse como ingresos, en realidad son
  // transferencias de cuentas bancarias para el pago de deudas"): un abono
  // en una tarjeta de crédito (o en cualquier otra cuenta de tipo deuda)
  // llega con tipo 'ingreso' porque reduce lo que se debe, pero no es
  // plata nueva entrando al negocio -- es el reflejo, del lado de la
  // deuda, de una transferencia que ya sale como egreso de alguna cuenta
  // bancaria. Mismos tipos que TIPOS_DEUDA en CuentasPage.js. Los gastos
  // (compras/consumos) de estas cuentas SÍ siguen contando como egresos
  // normales -- solo se excluyen los "ingresos".
  const TIPOS_CUENTA_DEUDA = [
    'tarjeta_credito',
    'prestamo_personal',
    'prestamo_hipotecario',
    'prestamo_auto',
    'prestamo_educacion',
    'prestamo_proveedores',
  ];
  const esCuentaDeDeuda = (accountId) => {
    const cuenta = cuentas.find((c) => c.id === accountId);
    return !!cuenta && TIPOS_CUENTA_DEUDA.includes(cuenta.tipo);
  };
  // "Ingreso real" = cuenta el monto como ingreso; un abono/pago en una
  // cuenta de deuda no lo es (ver comentario de arriba).
  const esIngresoReal = (tx) => tx.tipo === 'ingreso' && !esCuentaDeDeuda(tx.account_id);
  // 25 ago 2026 (Task #12 -- pedido de Augusto): los resúmenes y
  // subtotales de esta pantalla se muestran en Soles (ver formatAmount,
  // que antepone "S/" fijo) -- sumar directo un monto en dólares u otra
  // moneda a ese total sería incorrecto (mezclaría magnitudes distintas
  // sin convertir). Transaction.moneda viene vacío/null para la enorme
  // mayoría de movimientos (todo lo cargado a mano, y todo lo importado
  // antes de que el Importador de Estados de Cuenta empezara a traer
  // moneda_override para filas puntuales en otra moneda -- ver
  // StatementImporter.js) -- por eso "sin moneda" también cuenta como
  // soles, no solo 'PEN' explícito.
  //
  // 7 sep 2026 (Augusto: "la cuenta es de dólares, pero el símbolo sale en
  // soles"): el comentario de arriba asumía "sin moneda = soles", pero un
  // movimiento sin moneda de una cuenta en DÓLARES también caía como
  // soles y se mostraba con "S/". El backend define moneda=null como
  // "usa la moneda de la cuenta vinculada" (ver models/transaction.py),
  // así que acá se resuelve exactamente eso: moneda del movimiento si la
  // tiene, si no la de su cuenta, y solo si tampoco hay cuenta, soles.
  // Es retroactivo para todo el historial sin tocar la base de datos.
  const monedaDeTx = (tx) => {
    if (tx.moneda) return tx.moneda.toUpperCase();
    const cuenta = tx.account_id != null ? cuentas.find((c) => c.id === tx.account_id) : null;
    return (cuenta?.moneda || 'PEN').toUpperCase();
  };
  const esMonedaSoles = (tx) => monedaDeTx(tx) === 'PEN';
  // Monto con signo para sumas de neto/balance: ingreso real suma, gasto
  // resta, y un "ingreso" en cuenta de deuda no suma ni resta (no es un
  // movimiento de dinero real para el negocio). Los movimientos en otra
  // moneda se excluyen de este total (quedan aparte, ver
  // `resumen.montosExtranjeros` más abajo) para no mezclarlos con soles.
  // 3 sep 2026 (Augusto: transferencias entre cuentas/tarjetas -- ver
  // POST /transactions/transferencia): igual que un "ingreso" en una
  // cuenta de deuda (arriba), NINGÚN lado de una transferencia es
  // Ingreso/Gasto real para el resumen GENERAL -- ni el egreso de la
  // cuenta origen ni el ingreso de la cuenta destino, es la misma plata
  // movida de un bolsillo a otro. Dentro de un grupo de UNA sola cuenta
  // (montoNetoCuenta más abajo) sí debe contar, igual que un pago a
  // tarjeta -- ahí sí interesa el saldo real de esa cuenta.
  const esTransferencia = (tx) => tx.categoria === 'Transferencia';
  // 30 oct 2026 (Augusto: "¿por qué no cuadran los gastos con lo que
  // existe en los estados de cuenta por moneda?"; eligió "consumos, y
  // pagos aparte").
  //
  // Un ingreso en una tarjeta puede ser dos cosas: un PAGO (plata tuya que
  // viene de otra cuenta: no es consumo ni ingreso) o una DEVOLUCIÓN del
  // comercio (sí baja lo consumido, igual que en el extracto: "AMAZON
  // DEVOLUCION 35.99-"). Antes las dos se ignoraban; ahora la devolución
  // resta del consumo y el pago se muestra aparte, en la fila del grupo.
  const esPagoATarjeta = (tx) =>
    tx.tipo === 'ingreso' &&
    esCuentaDeDeuda(tx.account_id) &&
    (esTransferencia(tx) || /pago/i.test(tx.categoria || '') || /pago/i.test(tx.descripcion || '') || /pago/i.test(tx.proveedor || ''));
  const montoNeto = (tx) => {
    if (!esMonedaSoles(tx) || esTransferencia(tx)) return 0;
    if (tx.tipo === 'ingreso') {
      if (!esCuentaDeDeuda(tx.account_id)) return tx.monto;
      return esPagoATarjeta(tx) ? 0 : tx.monto; // devolución
    }
    return -tx.monto;
  };
  // 28 ago 2026 (pedido de Augusto: "por qué no se resta el depósito que
  // hice en el BBVA y muestra el saldo real" -- agrupando por Cuenta o
  // Fecha de corte, un pago/abono a la tarjeta no restaba nada del
  // subtotal del grupo): montoNeto (arriba) excluye a propósito los
  // "ingreso" de una cuenta de deuda del total, para no duplicar ese pago
  // como si fuera plata nueva en el resumen GENERAL de Ingresos/Egresos
  // (el egreso real ya se contó en la cuenta bancaria de origen -- ver
  // comentario de esCuentaDeDeuda más arriba). Pero dentro de un grupo que
  // ya está acotado a UNA sola cuenta (agrupando por Cuenta o por Fecha de
  // corte), no hay riesgo de duplicar nada -- ahí sí interesa ver el saldo
  // real de ESA tarjeta en ese ciclo: gastos menos pagos. Estas dos
  // variantes SÍ suman el pago (no lo excluyen); se usan en construirGrupos
  // solo cuando el grupo desciende de un nivel 'cuenta' o 'corte' (ver
  // cuentaEsAncestro ahí).
  const montoNetoCuenta = (tx) => {
    if (!esMonedaSoles(tx)) return 0;
    return tx.tipo === 'ingreso' ? tx.monto : -tx.monto;
  };
  // 26 ago 2026 (pedido de Augusto: "debe existir una columna adicional
  // para dólares"): esta columna nació convirtiendo todo a dólares con un
  // tipo de cambio en vivo (open.er-api.com). 28 ago 2026 (Augusto: "no
  // quiero que me conviertas nada, necesito el valor consumido en cada
  // moneda totalizado"): se revirtió esa conversión -- la columna
  // "Dólares" ahora es estrictamente lo que ya estaba cargado en dólares
  // (tx.moneda === 'USD'), sin tocar el tipo de cambio para nada, igual
  // que la columna en Soles ya solo suma lo que estaba en soles (ver
  // montoNeto/esMonedaSoles). Ya no hace falta llamar a ninguna API de
  // tipo de cambio.
  const esMonedaDolares = (tx) => monedaDeTx(tx) === 'USD';
  const formatDolares = (tx) => {
    if (!esMonedaDolares(tx)) return '—';
    return `$ ${formatAmountRaw(tx.monto)}`;
  };
  // Igual que montoNeto (arriba) pero solo para movimientos ya cargados en
  // dólares -- 0 para cualquier otra moneda (incluida soles), sin
  // conversión de ningún tipo.
  const montoNetoDolares = (tx) => {
    if (!esMonedaDolares(tx) || esTransferencia(tx)) return 0;
    if (tx.tipo === 'ingreso') {
      if (!esCuentaDeDeuda(tx.account_id)) return tx.monto;
      return esPagoATarjeta(tx) ? 0 : tx.monto; // devolución
    }
    return -tx.monto;
  };
  // Equivalente a montoNetoCuenta (arriba) pero en dólares -- ver ese
  // comentario para el porqué: dentro de un grupo acotado a una sola
  // cuenta, un pago a la tarjeta SÍ debe restar del saldo mostrado.
  const montoNetoDolaresCuenta = (tx) => {
    if (!esMonedaDolares(tx)) return 0;
    return tx.tipo === 'ingreso' ? tx.monto : -tx.monto;
  };
  // 3 ago 2026 (pedido de Augusto): al hacer clic en una cuenta desde el
  // Panel Financiero (NotionDashboard.js), se navega acá con
  // ?account_id=<id> en la URL -- se lee una sola vez al montar para
  // dejar el filtro de cuenta ya aplicado, en vez de aterrizar en la
  // tabla completa sin filtrar.
  const [searchParams, setSearchParams] = useSearchParams();
  // 30 oct 2026 (Augusto: "el search no funciona!!!!!"). El buscador del
  // encabezado era decorativo: no estaba conectado a nada. Ahora manda a
  // /transactions?q=texto, y acá ese texto filtra por descripción,
  // comercio, categoría, cuenta o monto.
  const textoBusqueda = (searchParams.get('q') || '').trim().toLowerCase();
  const quitarBusqueda = () => {
    const p = new URLSearchParams(searchParams);
    p.delete('q');
    setSearchParams(p);
  };
  // Filtros: por cuenta, categoría y tipo (ingreso/egreso), todos
  // client-side sobre la lista ya cargada — el backend actual no acepta
  // query params para esto (GET /transactions siempre trae todo).
  // 14 sep 2026: el ?account_id= de la URL tiene prioridad sobre lo
  // guardado en localStorage -- si Augusto hace clic en una cuenta desde
  // el Panel, quiere ver ESA cuenta, no la que tenía filtrada antes.
  //
  // 17 sep 2026: todos los filtros de abajo son LISTAS (ver comodín
  // `comoLista` arriba). Lista vacía = sin filtrar, que es lo que antes
  // significaba ''. Varios valores se combinan con O dentro del mismo
  // filtro (cuenta A **o** cuenta B) y con Y entre filtros distintos
  // (cuenta A y categoría Mercado) -- que es como la gente espera que
  // funcione, y lo que permite preguntas como "¿cuánto gasté en mercado y
  // restaurantes, entre mis dos tarjetas?".
  const [filtroCuenta, setFiltroCuenta] = useState(() => {
    const deLaUrl = searchParams.get('account_id');
    if (deLaUrl) return [deLaUrl];
    return comoLista(leerFiltrosGuardados()?.filtroCuenta);
  });
  const [filtroCategoria, setFiltroCategoria] = useState(
    () => comoLista(leerFiltrosGuardados()?.filtroCategoria)
  );
  const [filtroTipo, setFiltroTipo] = useState(() => leerFiltrosGuardados()?.filtroTipo || '');
  // 29 ago 2026 (pedido de Augusto: "toca añadir fechas de corte en
  // filtros y mes"): mismo mecanismo de filtro que Cuenta/Categoría/Tipo,
  // ver claveMesTx/claveCorteTx más arriba.
  const [filtroMes, setFiltroMes] = useState(() => comoLista(leerFiltrosGuardados()?.filtroMes));
  const [filtroCorte, setFiltroCorte] = useState(() => comoLista(leerFiltrosGuardados()?.filtroCorte));
  // 17 sep 2026 (pedido de Augusto: "necesitamos filtro por año"): mismo
  // mecanismo que filtroMes, ver claveAnioTx más arriba.
  const [filtroAnio, setFiltroAnio] = useState(() => comoLista(leerFiltrosGuardados()?.filtroAnio));
  // 4 sep 2026 (Augusto: "cuentas provisiones o ahorros intocables...
  // no incluirse en el calendario o transacciones a menos indicado por
  // el usuario"): por defecto, las cuentas es_reserva quedan fuera de la
  // lista/totales cuando se está viendo "todas las cuentas" (filtroCuenta
  // === ''). Si el usuario filtra directo por una cuenta de reserva
  // (filtroCuenta con su id), esa elección explícita siempre se respeta,
  // con o sin este toggle -- ver transaccionesFiltradas más abajo.
  const [incluirReservas, setIncluirReservas] = useState(() => leerFiltrosGuardados()?.incluirReservas || false);

  // 17 sep 2026 (Augusto: "falta una opción para borrar todas las
  // transacciones"). Borrar de a una no escala: con 300 movimientos mal
  // importados la salida que la gente termina usando es borrar su cuenta
  // entera y volver a registrarse, y eso se lleva puesto también
  // presupuestos, categorías y metas.
  //
  // Va acá abajo, después de filtroCuenta/filtroAnio, porque los usa: el
  // caso más común no es "quiero empezar de cero" sino "la importación de
  // esta tarjeta salió mal", así que se ofrece borrar solo lo que está
  // filtrado en pantalla. Antes de borrar se le pregunta al backend
  // cuántas son (GET /transactions/borrar-todas/preview) y se muestra el
  // número -- eso es lo que convierte un botón peligroso en uno usable.
  const [borrarTodasOpen, setBorrarTodasOpen] = useState(false);
  const [borrarTodasAlcance, setBorrarTodasAlcance] = useState('filtrado');
  const [borrarTodasPreview, setBorrarTodasPreview] = useState(null);
  const [borrarTodasPalabra, setBorrarTodasPalabra] = useState('');
  const [borrarTodasLoading, setBorrarTodasLoading] = useState(false);
  const [borrarTodasError, setBorrarTodasError] = useState(null);
  const [borrarTodasResultado, setBorrarTodasResultado] = useState(null);

  const hayFiltrosDeBorrado = filtroCuenta.length > 0 || filtroAnio.length > 0;

  const alcanceDeBorrado = useCallback(
    (alcance) => {
      if (alcance === 'todo' || !hayFiltrosDeBorrado) return {};
      return {
        ...(filtroCuenta.length > 0 ? { account_ids: filtroCuenta.map(Number) } : {}),
        ...(filtroAnio.length > 0 ? { anios: filtroAnio.map(Number) } : {}),
      };
    },
    [filtroCuenta, filtroAnio, hayFiltrosDeBorrado]
  );

  const cargarPreviewBorrado = useCallback(
    async (alcance) => {
      setBorrarTodasPreview(null);
      setBorrarTodasError(null);
      try {
        // Los parámetros se arman a mano porque axios serializa un array
        // como `account_ids[]=1&account_ids[]=2`, y FastAPI espera el
        // mismo nombre repetido sin corchetes.
        const qs = new URLSearchParams();
        Object.entries(alcanceDeBorrado(alcance)).forEach(([clave, valores]) => {
          valores.forEach((v) => qs.append(clave, v));
        });
        const { data } = await apiClient.get(
          `/transactions/borrar-todas/preview${qs.toString() ? `?${qs}` : ''}`
        );
        setBorrarTodasPreview(data);
      } catch (err) {
        setBorrarTodasError(err.response?.data?.detail || 'No se pudo calcular cuántas se borrarían.');
      }
    },
    [alcanceDeBorrado]
  );

  const handleOpenBorrarTodas = () => {
    const inicial = hayFiltrosDeBorrado ? 'filtrado' : 'todo';
    setBorrarTodasAlcance(inicial);
    setBorrarTodasPalabra('');
    setBorrarTodasResultado(null);
    setBorrarTodasOpen(true);
    cargarPreviewBorrado(inicial);
  };

  const handleCambiarAlcanceBorrado = (alcance) => {
    setBorrarTodasAlcance(alcance);
    setBorrarTodasPalabra('');
    cargarPreviewBorrado(alcance);
  };

  const handleConfirmBorrarTodas = async () => {
    setBorrarTodasLoading(true);
    setBorrarTodasError(null);
    try {
      // `esperadas` es el número que se le mostró al usuario en el
      // diálogo. Si el backend encuentra otra cantidad, no borra nada y
      // devuelve 409 -- red de seguridad contra que el filtro se
      // interprete distinto de lo que la persona vio. Ver
      // BorrarTodasRequest en transactions.py.
      const { data } = await apiClient.post('/transactions/borrar-todas', {
        confirmacion: borrarTodasPalabra.trim().toUpperCase(),
        esperadas: borrarTodasPreview?.count ?? null,
        ...alcanceDeBorrado(borrarTodasAlcance),
      });
      setBorrarTodasResultado(data);
      setBorrarTodasPalabra('');
      dispatch(fetchTransactions());
    } catch (err) {
      setBorrarTodasError(err.response?.data?.detail || 'No se pudieron eliminar las transacciones.');
    } finally {
      setBorrarTodasLoading(false);
    }
  };
  // Niveles de agrupación elegidos, EN ORDEN (el primero es el grupo más
  // externo) — ej. ['tipo', 'categoria', 'proveedor'] arma Egresos >
  // Alimentación > Wong, con un subtotal en cada nivel. Array vacío =
  // sin agrupar (tabla plana).
  const [nivelesAgrupacion, setNivelesAgrupacion] = useState(() => leerFiltrosGuardados()?.nivelesAgrupacion || []);
  // 29 ago 2026 (pedido de Augusto: "mejor el orden que se haga por
  // columna y que permita varios subordenes, digamos primero categoría
  // luego proveedor, luego monto"): reemplaza el Select único de
  // "Ordenar por" -- ahora se ordena haciendo clic en el encabezado de la
  // columna (ver renderEncabezadoOrdenable), y shift+clic AGREGA esa
  // columna como siguiente nivel de desempate en vez de reemplazar el
  // orden actual. `criteriosOrden` es un array en orden de prioridad,
  // ej. [{campo:'categoria',direccion:'asc'}, {campo:'proveedor',
  // direccion:'asc'}, {campo:'monto',direccion:'desc'}]. Se arranca igual
  // que el comportamiento anterior (fecha, descendente) para que nadie
  // note el cambio hasta que haga clic en un encabezado.
  const [criteriosOrden, setCriteriosOrden] = useState(
    () => leerFiltrosGuardados()?.criteriosOrden || [{ campo: 'fecha', direccion: 'desc' }]
  );
  const handleOrdenarPorColumna = (campo, shiftKey) => {
    setCriteriosOrden((prev) => {
      const idx = prev.findIndex((c) => c.campo === campo);
      if (shiftKey) {
        // Multi-orden: agrega esta columna como siguiente nivel, o invierte
        // su dirección si ya estaba entre los criterios.
        if (idx === -1) return [...prev, { campo, direccion: 'asc' }];
        return prev.map((c, i) => (i === idx ? { ...c, direccion: c.direccion === 'asc' ? 'desc' : 'asc' } : c));
      }
      // Clic normal: esta columna pasa a ser el ÚNICO criterio -- si ya
      // era el único, solo invierte la dirección (igual que antes con el
      // botón de flecha).
      if (prev.length === 1 && idx === 0) {
        return [{ campo, direccion: prev[0].direccion === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ campo, direccion: 'asc' }];
    });
  };
  // 29 ago 2026 (pedido de Augusto): orden de los GRUPOS entre sí (no de
  // las transacciones dentro de cada uno, eso es ordenarPor/ordenDireccion
  // de arriba) -- ver OPCIONES_ORDEN_GRUPOS y construirGrupos. 'total'
  // descendente reproduce el comportamiento de antes (mayor a menor
  // monto), así que nadie nota el cambio si no toca este control nuevo.
  const [ordenarGruposPor, setOrdenarGruposPor] = useState(() => leerFiltrosGuardados()?.ordenarGruposPor || 'total');
  const [ordenarGruposDireccion, setOrdenarGruposDireccion] = useState(
    () => leerFiltrosGuardados()?.ordenarGruposDireccion || 'desc'
  ); // 'asc' | 'desc'
  // 14 sep 2026: persiste automáticamente cualquier cambio en filtros,
  // agrupación y orden en localStorage -- así un F5 o cerrar/volver a abrir
  // la pestaña deja la vista tal como estaba, sin que el usuario tenga que
  // rearmarla. No se guarda `draggedNivel` porque es un estado transitorio
  // (solo existe mientras se arrastra un chip) que no tiene sentido
  // recordar entre sesiones.
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTROS_STORAGE_KEY,
        JSON.stringify({
          filtroCuenta,
          filtroCategoria,
          filtroTipo,
          filtroMes,
          filtroAnio,
          filtroCorte,
          incluirReservas,
          nivelesAgrupacion,
          criteriosOrden,
          ordenarGruposPor,
          ordenarGruposDireccion,
        })
      );
    } catch {
      // localStorage bloqueado (modo privado, cuota llena, etc.) -- no es
      // crítico, la pantalla sigue funcionando solo sin persistencia.
    }
  }, [
    filtroCuenta,
    filtroCategoria,
    filtroTipo,
    filtroMes,
    filtroAnio,
    filtroCorte,
    incluirReservas,
    nivelesAgrupacion,
    criteriosOrden,
    ordenarGruposPor,
    ordenarGruposDireccion,
  ]);
  const toggleNivelAgrupacion = (value) => {
    setNivelesAgrupacion((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  // 25 ago 2026 (pedido de Augusto: "mejorar este tema de agrupación y
  // hacerlo más interactivo, user friendly"): antes el orden de anidamiento
  // se definía por el orden en que se hacía clic en cada chip -- no había
  // forma de ver ni cambiar ese orden sin quitar y volver a elegir todo de
  // nuevo. Ahora los niveles YA elegidos se muestran como una lista
  // arrastrable (drag-and-drop nativo del navegador, sin librerías extra)
  // -- soltar un nivel sobre otro lo inserta en esa posición. `draggedNivel`
  // guarda cuál chip se está arrastrando en este momento.
  const [draggedNivel, setDraggedNivel] = useState(null);
  const handleDragStartNivel = (value) => (e) => {
    setDraggedNivel(value);
    // Necesario para que algunos navegadores (Firefox) permitan iniciar el
    // arrastre -- el dato en sí no se usa, ya se guardó en el estado.
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', value);
  };
  const handleDragOverNivel = (e) => {
    e.preventDefault(); // sin esto, onDrop nunca se dispara.
  };
  const handleDropNivel = (targetValue) => (e) => {
    e.preventDefault();
    setNivelesAgrupacion((prev) => {
      if (!draggedNivel || draggedNivel === targetValue) return prev;
      const sinArrastrado = prev.filter((v) => v !== draggedNivel);
      const idxDestino = sinArrastrado.indexOf(targetValue);
      sinArrastrado.splice(idxDestino, 0, draggedNivel);
      return sinArrastrado;
    });
    setDraggedNivel(null);
  };
  // 20 ago 2026 (pedido de Augusto): expandir/contraer cada grupo de la
  // tabla agrupada -- antes, con agrupación activa, la tabla siempre
  // mostraba TODAS las filas de TODOS los grupos sin forma de
  // colapsarlos. `gruposColapsados` guarda las "rutas" (nombre de grupo,
  // o "grupo padre|||grupo hijo" para subgrupos anidados) que el usuario
  // contrajo -- se usa la ruta completa (no un índice de posición) para
  // que el estado de colapso de un grupo no salte a otro grupo distinto
  // si el orden cambia (ej. al cambiar el criterio de orden). Se reinicia
  // solo cuando cambia la propia agrupación (nivelesAgrupacion), porque
  // las rutas antiguas ya no corresponden a nada en la agrupación nueva.
  // 28 ago 2026 (pedido de Augusto: "cuando se hace la agrupación tiene
  // que estar en Contraído por default"): el reinicio real (colapsar
  // TODAS las rutas recién construidas) se hace más abajo, en un segundo
  // useEffect que depende de gruposAnidados -- necesita la agrupación ya
  // armada (construirGrupos) para saber qué rutas existen, así que va
  // después de esa definición.
  const [gruposColapsados, setGruposColapsados] = useState(() => new Set());
  const toggleColapsoGrupo = (ruta) => {
    setGruposColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(ruta)) next.delete(ruta);
      else next.add(ruta);
      return next;
    });
  };
  // Recorre la agrupación anidada actual para juntar TODAS las rutas de
  // grupo posibles (en cualquier profundidad) -- lo usa "Contraer todo".
  const recolectarRutasDeGrupos = (grupos, prefijo) => {
    let rutas = [];
    (grupos || []).forEach((g) => {
      const ruta = prefijo ? `${prefijo}|||${g.nombre}` : g.nombre;
      rutas.push(ruta);
      if (g.subgrupos) rutas = rutas.concat(recolectarRutasDeGrupos(g.subgrupos, ruta));
    });
    return rutas;
  };
  const hayFiltrosActivos =
    Boolean(textoBusqueda) ||
    filtroCuenta.length > 0 ||
    filtroCategoria.length > 0 ||
    filtroTipo !== '' ||
    filtroMes.length > 0 ||
    filtroAnio.length > 0 ||
    filtroCorte.length > 0;
  // 14 sep 2026 (pedido de Augusto: botón para volver todo a como estaba
  // antes, ahora que filtros/agrupación/orden quedan guardados entre
  // sesiones): superset de hayFiltrosActivos -- éste solo mira los 5
  // filtros básicos (así el texto "N de M transacciones" de arriba no dice
  // "de M" solo porque alguien agrupó o cambió el orden, cosas que no
  // reducen la lista). `hayAlgoQuePersistirDistinto` en cambio también
  // cuenta agrupación/orden para decidir si mostrar el botón de reseteo.
  const hayAlgoQuePersistirDistinto =
    hayFiltrosActivos ||
    incluirReservas ||
    nivelesAgrupacion.length > 0 ||
    criteriosOrden.length !== 1 ||
    criteriosOrden[0]?.campo !== 'fecha' ||
    criteriosOrden[0]?.direccion !== 'desc' ||
    ordenarGruposPor !== 'total' ||
    ordenarGruposDireccion !== 'desc';
  const limpiarFiltros = () => {
    setFiltroCuenta([]);
    setFiltroCategoria([]);
    setFiltroTipo('');
    setFiltroMes([]);
    setFiltroAnio([]);
    setFiltroCorte([]);
    setIncluirReservas(false);
    setNivelesAgrupacion([]);
    setCriteriosOrden([{ campo: 'fecha', direccion: 'desc' }]);
    setOrdenarGruposPor('total');
    setOrdenarGruposDireccion('desc');
    try {
      localStorage.removeItem(FILTROS_STORAGE_KEY);
    } catch {
      // no crítico -- si falla, el próximo useEffect igual sobrescribe la
      // clave con los valores ya reseteados.
    }
  };
  // 4 sep 2026: ids de cuentas marcadas es_reserva -- ver comentario junto
  // a incluirReservas más arriba.
  const reservaIds = useMemo(
    () => new Set(cuentas.filter((c) => c.es_reserva).map((c) => c.id)),
    [cuentas]
  );
  const transaccionesFiltradas = useMemo(() => {
    return transactions.filter((tx) => {
      // Dentro de un filtro, varios valores se combinan con O; entre
      // filtros distintos, con Y. Las cuentas se comparan como texto
      // porque el id del <Select> puede llegar como número o como string
      // (de la URL) y === los distinguiría.
      if (filtroCuenta.length > 0 && !filtroCuenta.map(String).includes(String(tx.account_id || ''))) return false;
      // Solo aplica cuando se está viendo "todas las cuentas" -- si el
      // usuario ya filtró por cuentas puntuales arriba (sean o no
      // reserva), esa elección explícita manda y esto no interviene.
      if (filtroCuenta.length === 0 && !incluirReservas && reservaIds.has(tx.account_id)) return false;
      if (filtroCategoria.length > 0 && !filtroCategoria.includes(tx.categoria || 'Otro')) return false;
      if (filtroTipo !== '' && tx.tipo !== filtroTipo) return false;
      if (filtroMes.length > 0 && !filtroMes.includes(claveMesTx(tx))) return false;
      if (filtroAnio.length > 0 && !filtroAnio.includes(claveAnioTx(tx))) return false;
      if (filtroCorte.length > 0 && !filtroCorte.includes(claveCorteTx(tx))) return false;
      if (textoBusqueda) {
        const cuenta = cuentas.find((c) => c.id === tx.account_id);
        const pajar = [tx.descripcion, tx.proveedor, tx.categoria, cuenta?.nombre, String(tx.monto ?? '')]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!pajar.includes(textoBusqueda)) return false;
      }
      return true;
    });
  }, [
    textoBusqueda,
    transactions,
    filtroCuenta,
    filtroCategoria,
    filtroTipo,
    filtroMes,
    filtroAnio,
    filtroCorte,
    cuentas,
    incluirReservas,
    reservaIds,
  ]);
  // 30 ago 2026 (pedido de Augusto: "cuando marques transacciones, se
  // deben de sumar"): si hay filas marcadas con el checkbox, el total de
  // abajo pasa a sumar SOLO esas filas en vez de todas las filtradas --
  // útil para, por ejemplo, marcar unas cuantas transacciones sueltas y
  // ver cuánto suman entre ellas sin tener que armar un filtro nuevo.
  // Sin nada marcado, se comporta igual que antes (suma todo lo filtrado).
  const transaccionesParaTotal = useMemo(
    () =>
      seleccionadas.size > 0
        ? transaccionesFiltradas.filter((tx) => seleccionadas.has(tx.id))
        : transaccionesFiltradas,
    [transaccionesFiltradas, seleccionadas]
  );
  const totalFiltrado = useMemo(
    () => transaccionesParaTotal.reduce((acc, tx) => acc + montoNeto(tx), 0),
    [transaccionesParaTotal, cuentas]
  );
  // 28 ago 2026 (pedido de Augusto: "pon los montos, Soles y Dólares en la
  // misma línea... cambia PEN por TOTALES"): equivalente a totalFiltrado
  // pero en dólares (ver montoNetoDolares) -- se muestra junto al total en
  // Soles en la etiqueta "TOTALES" de abajo, en vez de tener que agrupar
  // por Moneda para verlo aparte.
  const totalFiltradoDolares = useMemo(
    () => transaccionesParaTotal.reduce((acc, tx) => acc + montoNetoDolares(tx), 0),
    [transaccionesParaTotal, cuentas]
  );
  // 5 ago 2026 (Augusto: "el total de ingresos y egresos que aparece en
  // transacciones cada vez que se seleccione una cuenta, debe dar el
  // total de esa cuenta, no de todas las cuentas"): las tarjetas de
  // Ingresos/Egresos/Balance neto de arriba ahora se calculan sobre
  // `transaccionesFiltradas` (respeta el filtro de Cuenta, y de paso
  // también Categoría/Tipo si están activos) en vez de sobre `transactions`
  // completo -- para volver a ver el total de todas las cuentas basta con
  // el filtro "Todas las cuentas" que ya existe en el dropdown de Cuenta.
  //
  // 25 ago 2026 (Task #12): los movimientos en otra moneda (ver
  // esMonedaSoles arriba) ya no se suman directo a totalIncome/
  // totalExpenses (que se muestran con "S/" fijo, ver formatAmount) --
  // se acumulan aparte en `montosExtranjeros`, agrupados por moneda y
  // tipo, para mostrarlos como nota separada en vez de mezclarlos con el
  // total en soles.
  const resumen = useMemo(() => {
    let totalIncome = 0;
    let totalExpenses = 0;
    const montosExtranjeros = {};
    transaccionesFiltradas.forEach((tx) => {
      if (!esMonedaSoles(tx)) {
        const moneda = monedaDeTx(tx);
        const key = `${moneda}-${tx.tipo}`;
        if (!montosExtranjeros[key]) {
          montosExtranjeros[key] = { moneda, tipo: tx.tipo, total: 0, count: 0 };
        }
        montosExtranjeros[key].total += tx.monto || 0;
        montosExtranjeros[key].count += 1;
        return;
      }
      if (esTransferencia(tx)) return;
      if (esIngresoReal(tx)) totalIncome += tx.monto || 0;
      else if (tx.tipo === 'gasto') totalExpenses += tx.monto || 0;
      // 30 oct 2026: una devolución del comercio a la tarjeta baja lo
      // gastado (como en el extracto y en la fila TOTALES de la tabla). Sin
      // esto, Egresos y TOTALES diferían justo en las devoluciones.
      else if (tx.tipo === 'ingreso' && !esPagoATarjeta(tx)) totalExpenses -= tx.monto || 0;
    });
    return { totalIncome, totalExpenses, netBalance: totalIncome - totalExpenses, montosExtranjeros };
  }, [transaccionesFiltradas, cuentas]);
  // Orden aplicado a las transacciones (dentro de cada grupo, o a toda la
  // tabla cuando no hay agrupación) — por fecha, monto, categoría,
  // proveedor o tipo, ascendente o descendente. Ahora soporta VARIOS
  // niveles (criteriosOrden, ver arriba): compara por el primer criterio,
  // y si empatan pasa al siguiente -- así "categoría, luego proveedor,
  // luego monto" ordena proveedores dentro de cada categoría, y montos
  // dentro de cada proveedor.
  const valorDeCampoOrden = (tx, campo) => {
    switch (campo) {
      case 'monto': return tx.monto || 0;
      case 'categoria': return (tx.categoria || '').toLowerCase();
      case 'proveedor': return (tx.proveedor || '').toLowerCase();
      case 'tipo': return tx.tipo || '';
      case 'fecha':
      default: return tx.fecha ? new Date(tx.fecha).getTime() : 0;
    }
  };
  const transaccionesOrdenadas = useMemo(() => {
    return [...transaccionesFiltradas].sort((a, b) => {
      for (const { campo, direccion } of criteriosOrden) {
        const signo = direccion === 'asc' ? 1 : -1;
        const va = valorDeCampoOrden(a, campo);
        const vb = valorDeCampoOrden(b, campo);
        if (va < vb) return -1 * signo;
        if (va > vb) return 1 * signo;
      }
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaccionesFiltradas, criteriosOrden]);
  // 7 sep 2026: si el filtro/orden cambia, la página anterior puede
  // quedar fuera de rango (ej. estabas en la página 5 con "Todas las
  // cuentas" y filtras a una cuenta con solo 1 página) -- se vuelve a la
  // página 0 cada vez que cambia el conjunto de transacciones a mostrar.
  useEffect(() => {
    setPagina(0);
  }, [transaccionesOrdenadas]);
  // Solo la porción de transaccionesOrdenadas que corresponde a la
  // página actual -- ver comentario junto a "pagina"/"filasPorPagina"
  // más arriba sobre por qué hacía falta esto.
  const transaccionesPaginadas = useMemo(
    () => transaccionesOrdenadas.slice(pagina * filasPorPagina, pagina * filasPorPagina + filasPorPagina),
    [transaccionesOrdenadas, pagina, filasPorPagina]
  );
  // Etiqueta de un movimiento para un nivel de agrupación dado — "mes"
  // agrupa por fecha (mes y año) en vez de por día exacto, porque agrupar
  // por fecha exacta casi nunca junta más de un movimiento.
  const claveDeNivel = (tx, nivel) => {
    switch (nivel) {
      case 'tipo': return tx.tipo === 'ingreso' ? (language === 'es' ? 'Ingresos' : 'Income') : (language === 'es' ? 'Egresos' : 'Expenses');
      case 'categoria': return traducirCategoria(tx.categoria, language) || (language === 'en' ? 'Other' : 'Otro');
      case 'proveedor': return tx.proveedor || (language === 'es' ? 'Sin proveedor' : 'No merchant');
      // 4 ago 2026 (pedido de Augusto): niveles adicionales de
      // sub-agrupación, usando `cuentas` (ya cargada desde
      // /dashboard/accounts) para resolver nombre y moneda.
      case 'cuenta': return cuentaNombre(tx.account_id) || (language === 'es' ? 'Sin cuenta' : 'No account');
      // 28 ago 2026 (pedido de Augusto: "necesito ver qué categorías tienen
      // transacciones en cada moneda... una línea en Soles y una abajo con
      // Dólares"): esta agrupación antes usaba la moneda DE LA CUENTA
      // vinculada (cuenta?.moneda) -- pero casi todas las cuentas están
      // declaradas en Soles, así que casi todo caía en un solo grupo
      // "Soles" aunque la transacción en sí tuviera una moneda distinta
      // (moneda_override de una fila puntual importada de un estado de
      // cuenta en dólares, ver StatementImporter.js). Se usa tx.moneda
      // directo, igual que esMonedaSoles/esMonedaDolares más arriba, para
      // que el grupo refleje la moneda REAL de cada movimiento y no la de
      // la cuenta a la que está asociado.
      case 'moneda': return nombreMoneda(monedaDeTx(tx), language);
      case 'anio': {
        if (!tx.fecha) return language === 'es' ? 'Sin fecha' : 'No date';
        return String(new Date(tx.fecha).getFullYear());
      }
      case 'mes': {
        if (!tx.fecha) return language === 'es' ? 'Sin fecha' : 'No date';
        const d = new Date(tx.fecha);
        const label = d.toLocaleDateString(language === 'es' ? 'es-PE' : 'en-US', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
      }
      // 28 ago 2026 (pedido de Augusto): agrupa por el ciclo de
      // facturación real de la tarjeta, no por mes calendario -- ver
      // fechaCierreDelCiclo más arriba. Se incluye el nombre de la cuenta
      // en la etiqueta porque dos tarjetas distintas pueden cerrar el
      // mismo día (ej. dos VISA de bancos distintos, ambas el 25), y sin
      // el nombre sus ciclos se mezclarían en un solo grupo.
      case 'corte': {
        const cuenta = cuentas.find((c) => c.id === tx.account_id);
        if (!cuenta) return language === 'es' ? 'Sin cuenta' : 'No account';
        if (!tx.fecha) return language === 'es' ? `${cuenta.nombre} — Sin fecha` : `${cuenta.nombre} — No date`;
        const fechaCierre = cierreDeTx(tx);
        if (!fechaCierre) return language === 'es' ? `${cuenta.nombre} (sin día de cierre configurado)` : `${cuenta.nombre} (no statement day configured)`;
        const label = fechaCierre.toLocaleDateString(language === 'es' ? 'es-PE' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' });
        return language === 'es' ? `${cuenta.nombre} — Cierre ${label}` : `${cuenta.nombre} — Closing ${label}`;
      }
      default: return language === 'es' ? 'Otro' : 'Other';
    }
  };
  // Arma la agrupación anidada de forma recursiva: cada nivel de
  // nivelesAgrupacion produce un subgrupo dentro del anterior (ej. Tipo >
  // Categoría > Proveedor), cada uno con su propio subtotal, ordenados de
  // mayor a menor monto absoluto.
  //
  // 25 ago 2026 (Task #12): `otrasMonedasCount` cuenta cuántos movimientos
  // de este grupo están en una moneda distinta a soles -- quedan fuera
  // del `total` (ver montoNeto) pero se avisan como nota junto al
  // subtotal en renderGrupo, para que no parezca que el grupo tiene menos
  // movimientos de los que en realidad tiene.
  //
  // 28 ago 2026 (pedido de Augusto): `nivelesAplicados` acumula los
  // niveles ya consumidos en esta rama de la recursión (incluido el de
  // este mismo grupo) -- permite saber si 'cuenta' o 'corte' ya se aplicó
  // en algún punto de la ruta (cuentaEsAncestro), igual criterio que
  // monedaEsAncestro en renderGrupo. Cuando sí, el grupo ya está acotado a
  // una sola cuenta y se usan montoNetoCuenta/montoNetoDolaresCuenta (SÍ
  // restan pagos a la tarjeta) en vez de montoNeto/montoNetoDolares (que
  // los excluyen a propósito para no duplicarlos en el resumen general).
  const construirGrupos = (items, niveles, nivelesAplicados = []) => {
    if (niveles.length === 0) return null;
    const [nivelActual, ...resto] = niveles;
    const nivelesHastaAqui = [...nivelesAplicados, nivelActual];
    // 30 oct 2026 (Augusto: "¿por qué no cuadran...?"): los niveles por
    // debajo de Cuenta sumaban gastos MENOS pagos, y los de arriba solo
    // gastos -- dos números distintos en la misma columna, y los hijos no
    // sumaban al padre. Ahora todos los niveles suman lo mismo: consumos
    // (menos devoluciones), igual que el extracto. Los pagos van aparte
    // (pagosSoles/pagosDolares) y el saldo real de la tarjeta sigue
    // viéndose al agrupar por Fecha de corte (calcularSaldoAcumulado).
    const fnTotal = montoNeto;
    const fnTotalDolares = montoNetoDolares;
    const map = new Map();
    items.forEach((tx) => {
      const k = claveDeNivel(tx, nivelActual);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(tx);
    });
    return Array.from(map.entries())
      .map(([nombre, grupoItems]) => {
        // 28 ago 2026: total en dólares del grupo (ver montoNetoDolares) --
        // suma directa de lo que ya estaba en dólares, sin conversión.
        // dolaresCount distingue "el grupo no tiene nada en dólares"
        // (muestra "—") de "el grupo neto en dólares dio exactamente 0"
        // (muestra "$ 0.00" real).
        let total = grupoItems.reduce((acc, tx) => acc + fnTotal(tx), 0);
        let totalDolares = grupoItems.reduce((acc, tx) => acc + fnTotalDolares(tx), 0);
        const pagosSoles = grupoItems
          .filter((tx) => esPagoATarjeta(tx) && esMonedaSoles(tx))
          .reduce((acc, tx) => acc + Number(tx.monto || 0), 0);
        const pagosDolares = grupoItems
          .filter((tx) => esPagoATarjeta(tx) && esMonedaDolares(tx))
          .reduce((acc, tx) => acc + Number(tx.monto || 0), 0);
        let esEstimado = false;
        // 21 oct 2026 (Augusto: "no suman las transacciones agrupadas").
        //
        // Tenía razón en lo que veía y el número no estaba mal: en "Fecha
        // de corte" el total del grupo es el saldo ACUMULADO de la tarjeta
        // a esa fecha (lo que él mismo pidió el 28 de agosto, igual al
        // "Línea utilizada" del estado de cuenta), no la suma de las filas
        // del ciclo. Son dos cifras distintas y las dos sirven.
        //
        // El error era de presentación: una columna llamada "Monto" que en
        // esa fila no suma lo que tiene debajo, sin nada que lo explique.
        // Se guarda también la suma de las filas para poder mostrar las
        // dos y decir cuál es cuál.
        const sumaDeFilas = total;
        const sumaDeFilasDolares = totalDolares;
        let esSaldoDeCorte = false;
        // 28 ago 2026 (pedido de Augusto): en el nivel "Fecha de corte"
        // específicamente (no en "Cuenta" sola), el subtotal no es la
        // actividad de esa ventana sino el saldo ACUMULADO real a esa
        // fecha -- ver calcularSaldoAcumulado. Solo se reemplaza si se
        // conoce al menos un estado de cuenta importado de esa cuenta
        // como ancla; si no, se deja el total de solo-esta-ventana de
        // arriba, marcado como estimado para que quede claro en pantalla.
        // 21 oct 2026 (Augusto: "estoy tratando de obtener el total de lo
        // consumido al final del corte de cada mes para Alimentos").
        //
        // Ahí se ve el problema de reemplazar el total por el saldo de la
        // tarjeta: si estás filtrando por una categoría, un tipo o un
        // proveedor, el encabezado del ciclo te muestra la DEUDA COMPLETA
        // de la tarjeta -- un número que no tiene nada que ver con las
        // filas que estás mirando. Filtrado a "Alimentos", decir
        // "S/ 5,145.29" al lado de tres compras de mercado no es confuso:
        // es falso en ese contexto.
        //
        // El saldo acumulado solo significa algo cuando se ven TODOS los
        // movimientos del ciclo. Con un filtro que quita filas, gana la
        // suma de lo que está a la vista.
        const vistaFiltradaPorContenido =
          (filtroCategoria && filtroCategoria.length > 0) ||
          (filtroTipo && filtroTipo.length > 0);
        if (nivelActual === 'corte' && grupoItems.length > 0 && !vistaFiltradaPorContenido) {
          const primerTx = grupoItems[0];
          const cuentaDelGrupo = cuentas.find((c) => c.id === primerTx.account_id);
          const fechaCierreGrupo = cierreDeTx(primerTx);
          if (cuentaDelGrupo && fechaCierreGrupo) {
            const haySoles = grupoItems.some((tx) => esMonedaSoles(tx));
            const hayDolares = grupoItems.some((tx) => esMonedaDolares(tx));
            const acumSoles = calcularSaldoAcumulado(cuentaDelGrupo.id, 'PEN', fechaCierreGrupo, montoNetoCuenta);
            const acumDolares = calcularSaldoAcumulado(cuentaDelGrupo.id, 'USD', fechaCierreGrupo, montoNetoDolaresCuenta);
            if (acumSoles) {
              total = acumSoles.saldo;
              esSaldoDeCorte = true;
              if (acumSoles.esEstimado) esEstimado = true;
            } else if (haySoles) {
              esEstimado = true;
            }
            if (acumDolares) {
              totalDolares = acumDolares.saldo;
              esSaldoDeCorte = true;
              if (acumDolares.esEstimado) esEstimado = true;
            } else if (hayDolares) {
              esEstimado = true;
            }
          }
        }
        // 29 ago 2026 (pedido de Augusto): referencia cronológica del
        // grupo para la opción "Fecha" de OPCIONES_ORDEN_GRUPOS -- el
        // movimiento MÁS ANTIGUO del grupo, para que "Fecha de corte" o
        // "Fecha (mes)" queden en orden cronológico real en vez de por
        // monto. Sirve igual para cualquier otro nivel (Tipo, Categoría,
        // etc.), aunque ahí el orden resultante sea menos intuitivo.
        const fechaOrdenGrupo = grupoItems.reduce((min, tx) => {
          if (!tx.fecha) return min;
          const t = new Date(tx.fecha).getTime();
          return min === null || t < min ? t : min;
        }, null);
        return {
          nombre,
          items: grupoItems,
          total,
          totalDolares,
          esEstimado,
          esSaldoDeCorte,
          sumaDeFilas,
          sumaDeFilasDolares,
          pagosSoles,
          pagosDolares,
          fechaOrdenGrupo,
          dolaresCount: grupoItems.filter((tx) => esMonedaDolares(tx)).length,
          otrasMonedasCount: grupoItems.filter((tx) => !esMonedaSoles(tx)).length,
          subgrupos: construirGrupos(grupoItems, resto, nivelesHastaAqui),
        };
      })
      .sort((a, b) => {
        const signo = ordenarGruposDireccion === 'asc' ? 1 : -1;
        let va;
        let vb;
        switch (ordenarGruposPor) {
          case 'nombre':
            va = a.nombre.toLowerCase();
            vb = b.nombre.toLowerCase();
            break;
          case 'fecha':
            va = a.fechaOrdenGrupo ?? 0;
            vb = b.fechaOrdenGrupo ?? 0;
            break;
          case 'total':
          default:
            va = Math.abs(a.total);
            vb = Math.abs(b.total);
        }
        if (va < vb) return -1 * signo;
        if (va > vb) return 1 * signo;
        return 0;
      });
  };
  const gruposAnidados = useMemo(
    () => (nivelesAgrupacion.length > 0 ? construirGrupos(transaccionesOrdenadas, nivelesAgrupacion) : null),
    [transaccionesOrdenadas, nivelesAgrupacion, ordenarGruposPor, ordenarGruposDireccion]
  );
  // 28 ago 2026 (pedido de Augusto): al elegir o cambiar la agrupación
  // (nivelesAgrupacion), los grupos recién armados arrancan CONTRAÍDOS --
  // mismo mecanismo que "Contraer todo" (recolectarRutasDeGrupos sobre
  // gruposAnidados). Depende solo de nivelesAgrupacion (no de
  // gruposAnidados ni de los datos) a propósito: así, editar/filtrar
  // transacciones sin tocar la agrupación no vuelve a contraer grupos que
  // el usuario ya expandió a mano.
  //
  // 21 sep 2026 (Augusto: "cuando se haga un filtro en Transacciones,
  // mostrar todo contraído, por defecto"): también al cambiar un filtro.
  // Filtrar es empezar una búsqueda nueva, y lo primero que quieres ver
  // es el mapa --los grupos y sus totales-- no cien filas sueltas. Se
  // listan los filtros uno por uno y NO `transaccionesOrdenadas`: si
  // dependiera de los datos, editar una transacción volvería a contraer
  // todo lo que acabas de abrir a mano.
  useEffect(() => {
    setGruposColapsados(new Set(recolectarRutasDeGrupos(gruposAnidados, '')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nivelesAgrupacion, filtroCuenta, filtroCategoria, filtroTipo, filtroMes, filtroAnio, filtroCorte]);
  // 28 sep 2026 (Augusto: "cuando se haga refresh en Transacciones,
  // tienes que mantener en Contraer los grupos").
  //
  // Al recargar la página, la agrupación se restaura de localStorage pero
  // las transacciones todavía no llegaron: el efecto de arriba corre con
  // gruposAnidados vacío, no encuentra ninguna ruta que contraer, y como
  // depende solo de la agrupación y los filtros --nunca de los datos-- no
  // vuelve a correr cuando los datos aparecen. Resultado: después de un
  // F5 salía todo expandido, justo lo contrario de la regla que rige el
  // resto de la pantalla.
  //
  // Esto lo cierra contrayendo la PRIMERA vez que hay grupos de verdad.
  // Corre una sola vez (yaContrajoAlCargar) a propósito: si dependiera de
  // los datos en general, editar una transacción volvería a contraer todo
  // lo que acabas de abrir a mano, que fue justo lo que se cuidó en
  // agosto al escribir el efecto de arriba.
  const yaContrajoAlCargar = useRef(false);
  useEffect(() => {
    if (yaContrajoAlCargar.current) return;
    const rutas = recolectarRutasDeGrupos(gruposAnidados, '');
    if (rutas.length === 0) return;
    yaContrajoAlCargar.current = true;
    setGruposColapsados(new Set(rutas));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruposAnidados]);
  // 20 ago 2026 (pedido de Augusto): "Expandir todo" / "Contraer todo" --
  // usa recolectarRutasDeGrupos (definida más arriba) sobre la agrupación
  // ya construida para este render, así que siempre refleja los grupos
  // realmente visibles ahora mismo (con los filtros/orden actuales).
  const contraerTodo = () => setGruposColapsados(new Set(recolectarRutasDeGrupos(gruposAnidados, '')));
  const expandirTodo = () => setGruposColapsados(new Set());
  // Selección múltiple: opera sobre lo que está visible con los filtros
  // actuales (transaccionesOrdenadas), no sobre la lista completa -- así
  // "seleccionar todas" no selecciona a ciegas transacciones ocultas por
  // un filtro.
  const idsVisibles = useMemo(() => transaccionesOrdenadas.map((t) => t.id), [transaccionesOrdenadas]);
  const todasSeleccionadas = idsVisibles.length > 0 && idsVisibles.every((id) => seleccionadas.has(id));
  const algunaSeleccionada = idsVisibles.some((id) => seleccionadas.has(id));
  const toggleSeleccionarTodas = () => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (todasSeleccionadas) idsVisibles.forEach((id) => next.delete(id));
      else idsVisibles.forEach((id) => next.add(id));
      return next;
    });
  };
  // 29 ago 2026 (pedido de Augusto: orden por columna con clic, y varios
  // niveles con shift+clic): encabezado clicable para las columnas que
  // aparecen en OPCIONES_ORDEN -- muestra una flechita cuando esa columna
  // es uno de los criterios activos, y un numerito de prioridad (1, 2, 3…)
  // solo cuando hay más de un nivel de orden a la vez, para no llenar de
  // ruido el caso más común (un solo criterio).
  const renderEncabezadoOrdenable = (campo, label, align) => {
    const idx = criteriosOrden.findIndex((c) => c.campo === campo);
    const activo = idx !== -1;
    return (
      <TableCell
        align={align}
        onClick={(e) => handleOrdenarPorColumna(campo, e.shiftKey)}
        sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'grey.200' } }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
          {label}
          {activo && (
            criteriosOrden[idx].direccion === 'asc'
              ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          )}
          {activo && criteriosOrden.length > 1 && (
            <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', ml: 0.2 }}>
              {idx + 1}
            </Typography>
          )}
        </Box>
      </TableCell>
    );
  };
  // 12 ago 2026: doble clic en cualquiera de estas celdas la vuelve
  // editable en el momento (sin abrir el diálogo). `editando(field)`
  // decide si ESTA celda de ESTA fila es la que está en edición ahora
  // mismo -- editingCell guarda un solo {id, field} a la vez, así que
  // solo una celda en toda la tabla puede estar editándose.
  const renderFilaTx = (tx) => {
    const editando = (field) => editingCell?.id === tx.id && editingCell?.field === field;
    return (
      <TableRow key={tx.id} hover selected={seleccionadas.has(tx.id)}>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={seleccionadas.has(tx.id)}
            onChange={() => toggleSeleccion(tx.id)}
          />
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'fecha')}>
          {editando('fecha') ? (
            <TextField
              autoFocus
              size="small"
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleEditKeyDown}
            />
          ) : (
            tx.fecha ? new Date(tx.fecha).toLocaleDateString() : '—'
          )}
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'tipo')}>
          {editando('tipo') ? (
            <Select
              autoFocus
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onClose={commitEdit}
            >
              <MenuItem value="ingreso">{language === 'es' ? 'Ingreso' : 'Income'}</MenuItem>
              <MenuItem value="gasto">{language === 'es' ? 'Egreso' : 'Expense'}</MenuItem>
            </Select>
          ) : (
            <Chip
              label={tx.tipo === 'ingreso' ? (language === 'es' ? 'Ingreso' : 'Income') : (language === 'es' ? 'Egreso' : 'Expense')}
              size="small"
              color={tx.tipo === 'ingreso' ? 'success' : 'error'}
              variant="outlined"
            />
          )}
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'categoria')}>
          {editando('categoria') ? (
            <Autocomplete
              freeSolo
              size="small"
              options={allCategorias}
              value={editValue}
              onChange={(e, value) => setEditValue(value || '')}
              onInputChange={(e, value, reason) => { if (reason === 'input') setEditValue(value); }}
              onBlur={commitEdit}
              renderInput={(params) => (
                <TextField {...params} autoFocus size="small" onKeyDown={handleEditKeyDown} />
              )}
              sx={{ minWidth: 150 }}
            />
          ) : (
            tx.categoria
              ? `${getCategoryEmoji(tx.categoria, emojiOverrides)} ${traducirCategoria(tx.categoria, language)}`
              : '—'
          )}
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'proveedor')}>
          {editando('proveedor') ? (
            <TextField
              autoFocus
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleEditKeyDown}
            />
          ) : (
            tx.proveedor || '—'
          )}
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'descripcion')}>
          {editando('descripcion') ? (
            <TextField
              autoFocus
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleEditKeyDown}
            />
          ) : (
            tx.descripcion || '—'
          )}
        </TableCell>
        <TableCell onDoubleClick={() => startEdit(tx, 'account_id')}>
          {editando('account_id') ? (
            <Select
              autoFocus
              size="small"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onClose={commitEdit}
            >
              <MenuItem value="">{language === 'es' ? 'Sin asociar' : 'Not linked'}</MenuItem>
              {cuentas.map((c) => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
            </Select>
          ) : (
            cuentaNombre(tx.account_id)
          )}
        </TableCell>
        <TableCell align="right" onDoubleClick={() => startEdit(tx, 'monto')}>
          {editando('monto') ? (
            <TextField
              autoFocus
              size="small"
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={handleEditKeyDown}
              sx={{ maxWidth: 110 }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: tx.tipo === 'ingreso' ? 'success.main' : 'error.main' }}
            >
              {tx.tipo === 'ingreso' ? '+' : '-'}{formatTxAmount(tx, monedaDeTx(tx))}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color="text.secondary">
            {formatDolares(tx) === '—' ? '—' : `${tx.tipo === 'ingreso' ? '+' : '-'}${formatDolares(tx)}`}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <ButtonGroup size="small" variant="text">
            <Tooltip title={language === 'es' ? 'Editar transacción' : 'Edit transaction'}>
              <IconButton size="small" color="primary" onClick={() => handleOpenEdit(tx)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* 28 ago 2026 (pedido de Augusto): duplicar esta transacción --
                abre "Agregar Transacción" ya precargado con estos mismos
                datos, listo para ajustar y guardar. */}
            <Tooltip title={language === 'es' ? 'Duplicar transacción' : 'Duplicate transaction'}>
              <IconButton size="small" onClick={() => handleDuplicateTx(tx)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={language === 'es' ? 'Eliminar transacción' : 'Delete transaction'}>
              <IconButton size="small" color="error" onClick={() => handleDeleteConfirmOpen(tx.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </TableCell>
      </TableRow>
    );
  };
  // Renderiza un grupo (y, recursivamente, sus subgrupos) con sangría
  // creciente por nivel de profundidad, terminando en las filas de
  // transacciones cuando ya no hay más niveles que anidar.
  //
  // 20 ago 2026 (pedido de Augusto): "ruta" (antes "keyPrefix") ahora es
  // la ruta completa de nombres de grupo (ej. "Egresos|||Alimentación"),
  // no un índice de posición -- así el estado de expandido/contraído de
  // CADA grupo (ver gruposColapsados más arriba) sigue correspondiendo al
  // mismo grupo aunque cambie el orden por un filtro u ordenamiento
  // distinto. Un clic en el encabezado del grupo (o en su flecha)
  // contrae/expande solo ese grupo -- mientras está contraído, no se
  // renderizan sus subgrupos ni sus transacciones, solo el encabezado con
  // el conteo y el subtotal.
  const renderGrupo = (grupo, depth, ruta) => {
    const nivelDeEsteGrupo = nivelesAgrupacion[depth];
    const colapsado = gruposColapsados.has(ruta);
    // 28 ago 2026 (Augusto: mismo "S/ 0.00 · +N en otra moneda" sin
    // sentido aparecía también en los SUBGRUPOS anidados dentro de
    // "Dólares" -- ej. agrupando Moneda > Fecha (mes), cada mes adentro de
    // "Dólares" también salía con ese ruido, porque antes solo se revisaba
    // si el nivel de ESE grupo puntual era "moneda" (nunca lo es para un
    // subgrupo de mes). Una vez que "Moneda" es CUALQUIER nivel ancestro
    // (no solo el de este grupo puntual), todo lo de acá abajo ya viene
    // filtrado 100% a una sola moneda por construcción -- así que el
    // criterio real es la pureza del grupo en sí (otrasMonedasCount), no
    // en qué nivel puntual estemos parados.
    const monedaEsAncestro = nivelesAgrupacion.slice(0, depth + 1).includes('moneda');
    const filas = [
      <TableRow
        key={`${ruta}-header`}
        hover
        onClick={() => toggleColapsoGrupo(ruta)}
        sx={{ bgcolor: depth === 0 ? 'grey.100' : 'grey.50', cursor: 'pointer' }}
      >
        <TableCell colSpan={7} sx={{ pl: 1 + depth * 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {colapsado ? (
              <ChevronRightIcon fontSize="small" color="action" />
            ) : (
              <ExpandMoreIcon fontSize="small" color="action" />
            )}
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {nivelDeEsteGrupo === 'categoria' ? `${getCategoryEmoji(grupo.nombre, emojiOverrides)} ` : ''}
              {grupo.nombre}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({grupo.items.length})
              </Typography>
            </Typography>
          </Box>
        </TableCell>
        {/* 28 ago 2026 (Augusto: "tampoco esto es necesario", sobre el
            "S/ 0.00 · +60 en otra moneda" que salía en la columna Monto
            del grupo "Dólares" al agrupar por Moneda): mismo criterio que
            la columna Dólares de arriba -- si el nivel de ESTE grupo ya es
            "Moneda" y el grupo es 100% de otra moneda (nada en soles), no
            hay nada que aportar acá (el total real ya está en la columna
            Dólares), así que se deja en blanco en vez de un "S/ 0.00" que
            no significa nada. */}
        <TableCell align="right">
          {monedaEsAncestro && grupo.otrasMonedasCount === grupo.items.length ? (
            <Typography variant="body2" color="text.secondary">—</Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: grupo.total >= 0 ? 'success.main' : 'error.main' }}>
                {formatAmount(grupo.total)}
              </Typography>
              {grupo.otrasMonedasCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  +{grupo.otrasMonedasCount} {language === 'es' ? 'en otra moneda' : 'in another currency'}
                </Typography>
              )}
              {/* 30 oct 2026: pagos a la tarjeta, aparte de los consumos. */}
              {grupo.pagosSoles > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                  {language === 'es' ? 'pagos: ' : 'payments: '}{formatAmount(grupo.pagosSoles)}
                </Typography>
              )}
              {/* 21 oct 2026 (Augusto: "no suman las transacciones
                  agrupadas"). En "Fecha de corte" este número es el saldo
                  ACUMULADO de la tarjeta a esa fecha, no la suma de las
                  filas del ciclo -- y nada lo decía. Ahora se nombra, y
                  debajo va la suma de lo que sí está a la vista. */}
              {grupo.esSaldoDeCorte && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'deuda total al corte' : 'total debt at closing'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {language === 'es' ? 'consumos del ciclo: ' : 'cycle charges: '}
                    {formatAmount(grupo.sumaDeFilas)}
                  </Typography>
                </>
              )}
              {/* 28 ago 2026 (pedido de Augusto): en "Fecha de corte", si no
                  hay un estado de cuenta importado que sirva de ancla para
                  esta fecha, el saldo mostrado es una estimación (solo la
                  actividad de este ciclo, sin arrastrar el saldo real de
                  ciclos anteriores) -- ver calcularSaldoAcumulado. */}
              {grupo.esEstimado && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                  {language === 'es' ? 'estimado (sin estado de cuenta importado)' : 'estimated (no imported statement)'}
                </Typography>
              )}
            </>
          )}
        </TableCell>
        {/* 28 ago 2026 (pedido de Augusto: "una columna con la sumatoria de
            lo consumido en Soles y otro de lo consumido en dólares"):
            total del grupo estrictamente en dólares (ver montoNetoDolares
            -- sin conversión, solo lo que ya estaba cargado en USD), para
            no tener que expandir el grupo solo para ver ese número.
            Cuando el grupo no tiene ningún movimiento en dólares
            (dolaresCount === 0) se deja en "—" en vez de "$ 0.00", igual
            criterio que la columna en Soles con otrasMonedasCount. */}
        <TableCell align="right">
          {monedaEsAncestro && grupo.otrasMonedasCount === 0 ? (
            <Typography variant="body2" color="text.secondary">—</Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: grupo.totalDolares >= 0 ? 'success.main' : 'error.main' }}>
                {grupo.dolaresCount === 0 ? '—' : `$ ${formatAmountRaw(grupo.totalDolares)}`}
              </Typography>
              {grupo.pagosDolares > 0 && (
                <Typography variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                  {language === 'es' ? 'pagos: ' : 'payments: '}$ {formatAmountRaw(grupo.pagosDolares)}
                </Typography>
              )}
            </>
          )}
        </TableCell>
        <TableCell />
      </TableRow>,
    ];
    if (!colapsado) {
      if (grupo.subgrupos) {
        grupo.subgrupos.forEach((sg) => filas.push(...renderGrupo(sg, depth + 1, `${ruta}|||${sg.nombre}`)));
      } else {
        grupo.items.forEach((tx) => filas.push(renderFilaTx(tx)));
      }
    }
    return filas;
  };
  // 5 ago 2026 (Augusto: "se puede ampliar la vista de la tabla para que
  // las columnas no se vean ajustadas"): "md" (900px) se quedaba corto
  // para una tabla de 8 columnas (Fecha/Tipo/Categoría/Proveedor/
  // Descripción/Cuenta/Monto/Acciones) -- "lg" (1200px) es el mismo ancho
  // que ya usan las otras páginas con tablas anchas (CalendarPage,
  // ClientesPage, RecurringPage).
  return (
    // 28 ago 2026 (pedido de Augusto: "veo muchos espacios en blanco...
    // scroll up and down tedioso"): este Container va DENTRO del
    // Container de NotionLayout.js (que ya pone su propio padding
    // alrededor de toda página) -- pt:4/pb:8 (32px/64px) sumaban ENCIMA
    // de ese padding exterior, antes de llegar siquiera al título. Se
    // baja a algo mínimo; el espaciado real entre secciones ya lo dan los
    // mb de cada Card de abajo.
    <Container maxWidth="lg" disableGutters sx={{ pt: 0, pb: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1.5, fontWeight: 600 }}>
        {language === 'es' ? 'Transacciones' : 'Transactions'}
      </Typography>
      {loading && (
        <Box sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {language === 'es' ? 'Cargando transacciones...' : 'Loading transactions...'}
          </Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ my: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.main">
            {language === 'es'
              ? `Error al cargar transacciones: ${typeof error === 'string' ? error : 'ocurrió un problema.'}`
              : `Error loading transactions: ${typeof error === 'string' ? error : 'something went wrong.'}`}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* 17 sep 2026 (pedido de Augusto: "eliminar de la sección
            Transacciones: Añadir Transacción, duplicar, y unificar
            categorías" -- toolbar recargada de botones que casi nadie
            tocaba, ver AskUserQuestion de esta misma sesión sobre CUÁL
            "duplicar" se refería): se sacaron "Agregar Transacción"
            (handleOpenNew), "Duplicar mes anterior" y "Unificar categorías
            duplicadas", junto con sus diálogos y estado -- ver este mismo
            archivo antes de este cambio si hace falta recuperarlos. Sigue
            existiendo la forma de crear una transacción (el ícono
            "Duplicar transacción" por fila reabre el mismo diálogo/estado
            openNew que usaba el botón quitado) y de unificar categorías
            (endpoint POST /transactions/normalize-categorias sigue intacto
            en el backend, solo se quitó el botón). "Eliminar transacciones
            duplicadas" SÍ se mantiene -- fue la única de las tres que
            Augusto no pidió quitar. */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* 28 ago 2026 (pedido de Augusto): limpia transacciones que ya
              quedaron duplicadas por reintentar una importación que ya se
              había completado antes -- ver handleConfirmDedup más arriba. */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteSweepIcon fontSize="small" />}
            onClick={handleOpenDedupDialog}
          >
            {language === 'es' ? 'Eliminar transacciones duplicadas' : 'Delete duplicate transactions'}
          </Button>
          {/* 17 sep 2026 (pedido de Augusto): borrar todo de una vez, o
              solo lo que esté filtrado en pantalla -- ver
              handleOpenBorrarTodas. */}
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteSweepIcon fontSize="small" />}
            onClick={handleOpenBorrarTodas}
          >
            {language === 'es' ? 'Borrar transacciones…' : 'Delete transactions…'}
          </Button>
          {/* 12 ago 2026 (pedido de Augusto): borrado masivo -- solo
              aparece cuando hay algo seleccionado, para no ensuciar la
              barra de botones el resto del tiempo. */}
          {seleccionadas.size > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={handleBulkDeleteOpen}
            >
              {language === 'es' ? `Eliminar seleccionadas (${seleccionadas.size})` : `Delete selected (${seleccionadas.size})`}
            </Button>
          )}
          {/* 30 ago 2026 (pedido de Augusto): "opción para marcar en la
              lista de transacciones que permita cambiar transacciones a
              recurrentes en masa" -- mismo criterio que el botón de
              borrado masivo de arriba. */}
          {seleccionadas.size > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EventRepeatIcon fontSize="small" />}
              onClick={handleBulkRecurringOpen}
            >
              {language === 'es' ? `Marcar como recurrente (${seleccionadas.size})` : `Mark as recurring (${seleccionadas.size})`}
            </Button>
          )}
        </Box>
        {textoBusqueda && (
          <Chip
            size="small"
            color="primary"
            label={language === 'es' ? `Buscando: "${searchParams.get('q')}"` : `Search: "${searchParams.get('q')}"`}
            onDelete={quitarBusqueda}
            sx={{ mr: 1 }}
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {seleccionadas.size > 0
            ? (language === 'es'
              ? `${seleccionadas.size} ${seleccionadas.size === 1 ? 'seleccionada' : 'seleccionadas'}`
              : `${seleccionadas.size} selected`)
            : (language === 'es'
              ? `${transaccionesFiltradas.length} ${transaccionesFiltradas.length === 1 ? 'transacción' : 'transacciones'}${
                  hayFiltrosActivos ? ` de ${transactions.length}` : ''
                }`
              : `${transaccionesFiltradas.length} ${transaccionesFiltradas.length === 1 ? 'transaction' : 'transactions'}${
                  hayFiltrosActivos ? ` of ${transactions.length}` : ''
                }`)}
          {/* 28 ago 2026 (pedido de Augusto: "pon los montos, Soles y
              Dólares en la misma línea... cambia PEN por TOTALES"): antes
              decía "Neto filtrado" y solo mostraba el total en Soles --
              ahora los dos totales (Soles y Dólares) van juntos en esta
              misma línea, sin necesidad de agrupar por Moneda.
              30 ago 2026: cuando hay filas marcadas, este total ya es el
              de las seleccionadas (ver transaccionesParaTotal más arriba),
              así que la etiqueta sigue diciendo "TOTALES" en ambos casos. */}
          {language === 'es' ? ' · TOTALES: ' : ' · TOTALS: '}
          <Box component="span" sx={{ fontWeight: 'bold', color: totalFiltrado >= 0 ? 'success.main' : 'error.main' }}>
            {formatAmount(totalFiltrado)}
          </Box>
          {' / '}
          <Box component="span" sx={{ fontWeight: 'bold', color: totalFiltradoDolares >= 0 ? 'success.main' : 'error.main' }}>
            {`$ ${formatAmountRaw(totalFiltradoDolares)}`}
          </Box>
          {/* 28 ago 2026 (Augusto: "no quiero que me conviertas nada"): se
              quitó la nota de "Tipo de cambio" -- la columna Dólares ya no
              convierte nada, así que mostrar un tipo de cambio acá sería
              engañoso. */}
        </Typography>
      </Box>
      {/* Filtros y agrupación */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* 17 sep 2026 (Augusto: "necesito poder filtrar por 2 o más
              valores"): los filtros de cuenta, categoría, mes, año y
              corte son de selección múltiple. Cada uno muestra un resumen
              en vez de la lista completa de valores elegidos -- con tres
              cuentas seleccionadas, el nombre de las tres no entra en el
              control y lo desarma. */}
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="filtro-cuenta-label">{language === 'es' ? 'Cuenta' : 'Account'}</InputLabel>
            <Select
              multiple
              labelId="filtro-cuenta-label"
              label={language === 'es' ? 'Cuenta' : 'Account'}
              value={filtroCuenta}
              onChange={(e) => setFiltroCuenta(e.target.value)}
              // El menú se hacía del ancho del control (190px) y los
              // nombres largos quedaban apretados contra el checkbox.
              MenuProps={{ PaperProps: { sx: { maxHeight: 380, minWidth: 320 } } }}
              renderValue={(sel) =>
                sel.length === 1
                  ? cuentaNombre(Number(sel[0]))
                  : (language === 'es' ? `${sel.length} cuentas` : `${sel.length} accounts`)
              }
            >
              {/* 25 sep 2026 (Augusto: "se ve pésimo la selección de
                  cuentas cuando se usa Filtros"): con dos American Express
                  y tres "Cuenta Digital BCP SOLES" en la lista, el nombre
                  solo no alcanza para saber cuál estás marcando. Debajo de
                  cada nombre va su identificador -- el código de cuenta
                  completo, o los últimos dígitos en las tarjetas. */}
              {cuentas.map((c) => {
                const identificador = c.numero_cuenta
                  || (c.ultimos_digitos ? `••••${c.ultimos_digitos}` : null);
                return (
                  <MenuItem key={c.id} value={c.id} sx={{ py: 0.5 }}>
                    <Checkbox size="small" checked={filtroCuenta.map(String).includes(String(c.id))} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {c.es_reserva ? '🔒 ' : ''}{c.nombre}
                      </Typography>
                      {(identificador || c.moneda) && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {[identificador, c.moneda].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* 4 sep 2026 (Augusto: cuentas de reserva/ahorro intocables) --
              solo tiene sentido cuando se ve "todas las cuentas" (si ya se
              filtró por una cuenta puntual, esa elección manda igual). */}
          {filtroCuenta.length === 0 && reservaIds.size > 0 && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={incluirReservas}
                  onChange={(e) => setIncluirReservas(e.target.checked)}
                />
              }
              label={<Typography variant="body2">{language === 'es' ? 'Incluir reservas 🔒' : 'Include reserves 🔒'}</Typography>}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="filtro-categoria-label">{language === 'es' ? 'Categoría' : 'Category'}</InputLabel>
            <Select
              multiple
              labelId="filtro-categoria-label"
              label={language === 'es' ? 'Categoría' : 'Category'}
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              renderValue={(sel) =>
                sel.length === 1
                  ? traducirCategoria(sel[0], language)
                  : (language === 'es' ? `${sel.length} categorías` : `${sel.length} categories`)
              }
            >
              {/* value se mantiene crudo (así sigue calzando con
                  tx.categoria al filtrar) -- solo el texto visible se
                  traduce, ver traducirCategoria. */}
              {allCategorias.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox size="small" checked={filtroCategoria.includes(c)} />
                  {getCategoryEmoji(c, emojiOverrides)} {traducirCategoria(c, language)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="filtro-tipo-label">{language === 'es' ? 'Tipo' : 'Type'}</InputLabel>
            <Select
              labelId="filtro-tipo-label"
              label={language === 'es' ? 'Tipo' : 'Type'}
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <MenuItem value="">{language === 'es' ? 'Ingresos y egresos' : 'Income and expenses'}</MenuItem>
              <MenuItem value="ingreso">{language === 'es' ? 'Solo ingresos' : 'Income only'}</MenuItem>
              <MenuItem value="gasto">{language === 'es' ? 'Solo egresos' : 'Expenses only'}</MenuItem>
            </Select>
          </FormControl>
          {/* 29 ago 2026 (pedido de Augusto: "toca añadir fechas de corte
              en filtros y mes"): mismo patrón que los filtros de arriba --
              opciones = valores distintos ya presentes en las
              transacciones (ver opcionesFiltroMes/opcionesFiltroCorte). */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="filtro-mes-label">{language === 'es' ? 'Mes' : 'Month'}</InputLabel>
            <Select
              multiple
              labelId="filtro-mes-label"
              label={language === 'es' ? 'Mes' : 'Month'}
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
              renderValue={(sel) =>
                sel.length === 1 ? sel[0] : (language === 'es' ? `${sel.length} meses` : `${sel.length} months`)
              }
            >
              {opcionesFiltroMes.map((m) => (
                <MenuItem key={m} value={m}>
                  <Checkbox size="small" checked={filtroMes.includes(m)} />
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* 17 sep 2026 (pedido de Augusto: "necesitamos filtro por
              año"): mismo patrón que Mes, pero solo el año -- útil para
              años completos o para no tener que elegir mes por mes en
              cuentas viejas. Independiente del filtro de Mes: se pueden
              combinar los dos (ej. Año=2025 + Mes=Marzo 2025 sigue dando
              el mismo resultado que solo Mes, pero Año solo ya sirve para
              ver el año entero). */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="filtro-anio-label">{language === 'es' ? 'Año' : 'Year'}</InputLabel>
            <Select
              multiple
              labelId="filtro-anio-label"
              label={language === 'es' ? 'Año' : 'Year'}
              value={filtroAnio}
              onChange={(e) => setFiltroAnio(e.target.value)}
              renderValue={(sel) => sel.join(', ')}
            >
              {opcionesFiltroAnio.map((a) => (
                <MenuItem key={a} value={a}>
                  <Checkbox size="small" checked={filtroAnio.includes(a)} />
                  {a}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="filtro-corte-label">{language === 'es' ? 'Fecha de corte' : 'Statement date'}</InputLabel>
            <Select
              multiple
              labelId="filtro-corte-label"
              label={language === 'es' ? 'Fecha de corte' : 'Statement date'}
              value={filtroCorte}
              onChange={(e) => setFiltroCorte(e.target.value)}
              renderValue={(sel) =>
                sel.length === 1 ? sel[0] : (language === 'es' ? `${sel.length} ciclos` : `${sel.length} cycles`)
              }
            >
              {opcionesFiltroCorte.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox size="small" checked={filtroCorte.includes(c)} />
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hayAlgoQuePersistirDistinto && (
            <Button size="small" onClick={limpiarFiltros}>{language === 'es' ? 'Restablecer filtros' : 'Reset filters'}</Button>
          )}
          <Box sx={{ flexGrow: 1, minWidth: '100%' }} />
          <Divider sx={{ width: '100%', my: 0.5 }} />
          {/* 25 ago 2026 (pedido de Augusto: "mejorar este tema de
              agrupación y hacerlo más interactivo, user friendly" +
              "separar Contraer todo/Expandir todo/Quitar agrupación en
              una zona diferente"): esta caja completa reemplaza a la fila
              única de chips sueltos + botones mezclados de antes. Ahora
              son dos filas bien diferenciadas: (1) los niveles YA
              elegidos, como una lista arrastrable para reordenar el
              anidamiento a simple vista, con los niveles disponibles para
              agregar debajo; (2) separado por su propio divisor, los
              controles de vista (Contraer/Expandir/Quitar), que solo
              aparecen si hay algo agrupado. */}
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'es'
                ? 'Agrupar por (arrastra los elegidos para reordenar el anidamiento):'
                : 'Group by (drag the chosen ones to reorder the nesting):'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 32 }}>
              {nivelesAgrupacion.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', alignSelf: 'center' }}>
                  {language === 'es' ? 'Sin agrupar — elige un nivel abajo para empezar.' : 'Not grouped — pick a level below to get started.'}
                </Typography>
              )}
              {nivelesAgrupacion.map((value, idx) => {
                const nivel = NIVELES_DISPONIBLES.find((n) => n.value === value);
                return (
                  <Chip
                    key={value}
                    draggable
                    onDragStart={handleDragStartNivel(value)}
                    onDragOver={handleDragOverNivel}
                    onDrop={handleDropNivel(value)}
                    onDragEnd={() => setDraggedNivel(null)}
                    icon={<DragIndicatorIcon fontSize="small" />}
                    color="primary"
                    variant="filled"
                    label={`${idx + 1}. ${nivel?.label || value}`}
                    onDelete={() => toggleNivelAgrupacion(value)}
                    sx={{
                      cursor: 'grab',
                      opacity: draggedNivel === value ? 0.4 : 1,
                      '&:active': { cursor: 'grabbing' },
                    }}
                  />
                );
              })}
            </Box>
            {NIVELES_DISPONIBLES.some((n) => !nivelesAgrupacion.includes(n.value)) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">{language === 'es' ? 'Agregar nivel:' : 'Add level:'}</Typography>
                {NIVELES_DISPONIBLES.filter((n) => !nivelesAgrupacion.includes(n.value)).map((n) => (
                  <Chip
                    key={n.value}
                    size="small"
                    clickable
                    variant="outlined"
                    icon={<AddIcon fontSize="small" />}
                    label={n.label}
                    onClick={() => toggleNivelAgrupacion(n.value)}
                  />
                ))}
              </Box>
            )}
            {nivelesAgrupacion.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {language === 'es' ? 'Vista de grupos:' : 'Group view:'}
                  </Typography>
                  {/* 20 ago 2026 (pedido de Augusto): expandir/contraer
                      TODOS los grupos de un clic, además de poder contraer
                      uno por uno haciendo clic en su encabezado (ver
                      renderGrupo). */}
                  <Button size="small" variant="outlined" startIcon={<UnfoldLessIcon fontSize="small" />} onClick={contraerTodo}>
                    {language === 'es' ? 'Contraer todo' : 'Collapse all'}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<UnfoldMoreIcon fontSize="small" />} onClick={expandirTodo}>
                    {language === 'es' ? 'Expandir todo' : 'Expand all'}
                  </Button>
                  <Button size="small" color="error" onClick={() => setNivelesAgrupacion([])}>
                    {language === 'es' ? 'Quitar agrupación' : 'Remove grouping'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
          <Divider sx={{ width: '100%', my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {language === 'es'
                ? 'Ordenar: clic en un encabezado de columna, shift+clic agrega otro nivel de orden.'
                : 'Sort: click a column header, shift+click adds another sort level.'}
            </Typography>
            {criteriosOrden.length > 1 && (
              <Chip
                size="small"
                variant="outlined"
                label={criteriosOrden
                  .map((c, i) => `${i + 1}. ${OPCIONES_ORDEN.find((o) => o.value === c.campo)?.label || c.campo}`)
                  .join('  ')}
                onDelete={() => setCriteriosOrden([{ campo: 'fecha', direccion: 'desc' }])}
              />
            )}
            {/* 29 ago 2026 (pedido de Augusto: "orden por cada tipo de
                nivel cuando salgan los resultados"): solo tiene sentido
                con agrupación activa -- sin agrupar, ya está el control
                de arriba (Ordenar por) para la tabla completa. Ver
                OPCIONES_ORDEN_GRUPOS/construirGrupos. */}
            {nivelesAgrupacion.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {language === 'es' ? 'Ordenar grupos por:' : 'Sort groups by:'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select value={ordenarGruposPor} onChange={(e) => setOrdenarGruposPor(e.target.value)}>
                    {OPCIONES_ORDEN_GRUPOS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <Tooltip title={ordenarGruposDireccion === 'asc' ? (language === 'es' ? 'Ascendente' : 'Ascending') : (language === 'es' ? 'Descendente' : 'Descending')}>
                  <IconButton
                    size="small"
                    onClick={() => setOrdenarGruposDireccion((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  >
                    {ordenarGruposDireccion === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small" aria-label={language === 'es' ? 'tabla de transacciones' : 'transactions table'}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={algunaSeleccionada && !todasSeleccionadas}
                  checked={todasSeleccionadas}
                  onChange={toggleSeleccionarTodas}
                />
              </TableCell>
              {renderEncabezadoOrdenable('fecha', language === 'es' ? 'Fecha' : 'Date')}
              {renderEncabezadoOrdenable('tipo', language === 'es' ? 'Tipo' : 'Type')}
              {renderEncabezadoOrdenable('categoria', language === 'es' ? 'Categoría' : 'Category')}
              {renderEncabezadoOrdenable('proveedor', language === 'es' ? 'Proveedor' : 'Merchant')}
              <TableCell>{language === 'es' ? 'Descripción' : 'Description'}</TableCell>
              <TableCell>{language === 'es' ? 'Cuenta' : 'Account'}</TableCell>
              {renderEncabezadoOrdenable('monto', language === 'es' ? 'Monto' : 'Amount', 'right')}
              <TableCell align="right">{language === 'es' ? 'Dólares' : 'Dollars'}</TableCell>
              <TableCell align="center">{language === 'es' ? 'Acciones' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gruposAnidados === null
              ? transaccionesPaginadas.map((tx) => renderFilaTx(tx))
              : gruposAnidados.flatMap((grupo) => renderGrupo(grupo, 0, grupo.nombre))}
            {transaccionesFiltradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {language === 'es' ? 'No hay transacciones que coincidan con estos filtros.' : 'No transactions match these filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {/* 17 sep 2026 (pedido de Augusto: "en transacciones siempre hacer
              una sumatoria al final de ambas monedas, no solamente cuando
              se hacen agrupaciones"): antes el único total de ambas
              monedas SIEMPRE visible era la línea de arriba de la tabla
              (' · TOTALES: ', ver totalFiltrado/totalFiltradoDolares) --
              con la tabla sin agrupar, ese total quedaba lejos después de
              scrollear varias páginas/filas, y con agrupación cada grupo
              muestra SU propio subtotal pero no había un total de TODO
              abajo. Este pie se ve siempre (agrupado o no, con o sin
              selección) y usa los mismos totales que la línea de arriba,
              así que nunca pueden mostrar números distintos. */}
          {transaccionesFiltradas.length > 0 && (
            <TableFooter>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell colSpan={7} sx={{ fontWeight: 'bold' }}>
                  {language === 'es'
                    ? (seleccionadas.size > 0 ? `TOTALES (${seleccionadas.size} seleccionadas)` : 'TOTALES')
                    : (seleccionadas.size > 0 ? `TOTALS (${seleccionadas.size} selected)` : 'TOTALS')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: totalFiltrado >= 0 ? 'success.main' : 'error.main' }}>
                  {formatAmount(totalFiltrado)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: totalFiltradoDolares >= 0 ? 'success.main' : 'error.main' }}>
                  {`$ ${formatAmountRaw(totalFiltradoDolares)}`}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      {/* 7 sep 2026: paginación de la vista sin agrupar -- ver el
          comentario junto a "pagina"/"filasPorPagina" arriba. Solo tiene
          sentido cuando gruposAnidados es null; con agrupación activa el
          conteo total ya lo maneja cada grupo. */}
      {gruposAnidados === null && (
        <TablePagination
          component="div"
          count={transaccionesOrdenadas.length}
          page={pagina}
          onPageChange={(e, nuevaPagina) => setPagina(nuevaPagina)}
          rowsPerPage={filasPorPagina}
          onRowsPerPageChange={(e) => {
            setFilasPorPagina(parseInt(e.target.value, 10));
            setPagina(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage={language === 'es' ? 'Filas por página' : 'Rows per page'}
          labelDisplayedRows={({ from, to, count }) => (language === 'es' ? `${from}–${to} de ${count}` : `${from}–${to} of ${count}`)}
          sx={{ mb: 2 }}
        />
      )}
      {/* 30 ago 2026 (Augusto: "en vez que se sumarice arriba lo
          seleccionado, debería estar debajo de las transacciones"): esta
          tarjeta de Ingresos/Egresos/Balance neto vivía arriba de la
          tabla, antes de los filtros -- se movió acá abajo para que quede
          junto a lo que resume (respeta el filtro de Cuenta/Categoría/Tipo
          y, si hay filas seleccionadas, se acota a esas -- ver
          transaccionesParaTotal). Sin cambios en el cálculo, solo de
          posición. */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* 5 ago 2026: aclara de qué alcance son los totales de abajo,
              ahora que respetan el filtro de Cuenta (y Categoría/Tipo si
              están activos) en vez de mostrar siempre todas las cuentas. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {/* 17 sep 2026: con selección múltiple se listan los nombres,
                que es lo que hace falta para saber qué está sumando el
                total de abajo. */}
            {filtroCuenta.length > 0
              ? `${language === 'es' ? 'Cuentas' : 'Accounts'}: ${filtroCuenta.map((id) => cuentaNombre(Number(id))).join(', ')}`
              : (language === 'es' ? 'Todas las cuentas' : 'All accounts')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">{language === 'es' ? 'Ingresos' : 'Income'}</Typography>
              <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                {formatAmount(resumen.totalIncome)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">{language === 'es' ? 'Egresos' : 'Expenses'}</Typography>
              <Typography variant="h5" color="error.main" sx={{ fontWeight: 'bold' }}>
                {formatAmount(resumen.totalExpenses)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">{language === 'es' ? 'Balance neto' : 'Net balance'}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {formatAmount(resumen.netBalance)}
              </Typography>
            </Grid>
          </Grid>
          {/* 25 ago 2026 (Task #12): movimientos en otra moneda, excluidos
              de los totales en soles de arriba (ver esMonedaSoles) -- se
              muestran aparte para que no se pierdan de vista, sin
              mezclarlos con un total que está etiquetado "S/". */}
          {Object.values(resumen.montosExtranjeros).length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {language === 'es' ? 'No incluido arriba (otras monedas):' : 'Not included above (other currencies):'}{' '}
              {Object.values(resumen.montosExtranjeros)
                .map((m) => `${m.tipo === 'ingreso' ? '+' : '-'}${getCurrencySymbol(m.moneda)} ${formatAmountRaw(m.total)} (${m.count})`)
                .join(' · ')}
            </Typography>
          )}
        </CardContent>
      </Card>
      {/* Diálogo nueva transacción */}
      <Dialog open={openNew} onClose={handleCloseNew} fullWidth maxWidth="sm">
        <DialogTitle>{language === 'es' ? 'Agregar Transacción' : 'Add Transaction'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="tipo-tx-label">{language === 'es' ? 'Tipo' : 'Type'}</InputLabel>
            <Select
              labelId="tipo-tx-label"
              name="tipo"
              value={nuevaTx.tipo}
              label={language === 'es' ? 'Tipo' : 'Type'}
              onChange={handleChange}
            >
              <MenuItem value="ingreso">{language === 'es' ? 'Ingreso' : 'Income'}</MenuItem>
              <MenuItem value="gasto">{language === 'es' ? 'Egreso' : 'Expense'}</MenuItem>
            </Select>
          </FormControl>
          {/* 28 sep 2026 (Augusto: "metí un egreso de CAD$1379... no solo
              no lo convierte a dólares americanos, sino que lo pone en
              moneda local").
              El formulario NO tenía selector de moneda: siempre usaba la
              de la cuenta elegida. Escribiste 1379 en una tarjeta en
              soles y quedaron S/ 1,379. */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              margin="dense"
              name="monto"
              label={language === 'es' ? 'Monto' : 'Amount'}
              type="number"
              fullWidth
              required
              value={nuevaTx.monto}
              onChange={handleChange}
            />
            <FormControl margin="dense" sx={{ minWidth: 130 }}>
              <InputLabel id="moneda-tx-label">{language === 'es' ? 'Moneda' : 'Currency'}</InputLabel>
              <Select
                labelId="moneda-tx-label"
                name="moneda"
                label={language === 'es' ? 'Moneda' : 'Currency'}
                value={nuevaTx.moneda || monedaDeCuenta(nuevaTx.account_id) || 'PEN'}
                onChange={handleChange}
              >
                {monedasDisponibles.map((m) => (
                  <MenuItem key={m.codigo} value={m.codigo}>
                    {m.codigo} — {m.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Se muestra el equivalente ANTES de guardar: ver el número es
              la diferencia entre confiar en él y descubrir semanas después
              que se guardó cualquier cosa. */}
          {conversionPreview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {language === 'es' ? (
                <>
                  Se guardará como <strong>US$ {conversionPreview.monto_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                  {' '}(tasa {conversionPreview.tasa}). Es un estimado con la tasa de mercado del día;
                  tu banco suma su comisión de cambio. Cuando importes el extracto, PCM lo reemplaza
                  por la cifra real.
                </>
              ) : (
                <>
                  Will be saved as <strong>US$ {conversionPreview.monto_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                  {' '}(rate {conversionPreview.tasa}). This is an estimate at today market rate; your
                  bank adds its FX fee. When you import the statement, PCM replaces it with the real figure.
                </>
              )}
            </Alert>
          )}
          {errorConversion && <Alert severity="warning" sx={{ mb: 2 }}>{errorConversion}</Alert>}
          {!nuevaTx.es_recurrente && (
            <Autocomplete
              freeSolo
              options={proveedoresConocidos}
              value={nuevaTx.proveedor || ''}
              onChange={handleProveedorChange(setNuevaTx)}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') handleProveedorChange(setNuevaTx)(e, value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label={language === 'es' ? 'Proveedor (opcional)' : 'Merchant (optional)'}
                  helperText={language === 'es'
                    ? 'Ej. Wong, Uber, Netflix — si ya lo usaste antes, se auto-completa la categoría.'
                    : "E.g. Wong, Uber, Netflix — if you've used it before, the category auto-fills."}
                  fullWidth
                />
              )}
              sx={{ mb: 2 }}
            />
          )}
          <CategoriaPicker
            value={nuevaTx.categoria}
            onChange={(cat) => setNuevaTx((prev) => ({ ...prev, categoria: cat }))}
            language={language}
          />
          <Autocomplete
            freeSolo
            options={allCategorias}
            value={nuevaTx.categoria}
            onChange={(e, value) => setNuevaTx((prev) => ({ ...prev, categoria: value || '' }))}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') setNuevaTx((prev) => ({ ...prev, categoria: value }));
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <span style={{ marginRight: 8 }}>{getCategoryEmoji(option)}</span>
                {traducirCategoria(option, language)}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} margin="dense" label={language === 'es' ? 'Categoría' : 'Category'} fullWidth />
            )}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="cuenta-tx-label">{language === 'es' ? 'Cuenta (opcional)' : 'Account (optional)'}</InputLabel>
            <Select
              labelId="cuenta-tx-label"
              name="account_id"
              value={nuevaTx.account_id}
              label={language === 'es' ? 'Cuenta (opcional)' : 'Account (optional)'}
              onChange={handleChange}
            >
              <MenuItem value="">{language === 'es' ? 'Sin asociar' : 'Not linked'}</MenuItem>
              {cuentas.map((c) => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
              <MenuItem value="__crear_cuenta__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                + {language === 'es' ? 'Agregar cuenta nueva' : 'Add new account'}
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="descripcion"
            label={language === 'es' ? 'Descripción' : 'Description'}
            type="text"
            fullWidth
            value={nuevaTx.descripcion}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={nuevaTx.es_recurrente}
                onChange={(e) => setNuevaTx((prev) => ({ ...prev, es_recurrente: e.target.checked }))}
              />
            }
            label={language === 'es' ? '¿Es recurrente?' : 'Is it recurring?'}
          />
          {nuevaTx.es_recurrente && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {language === 'es'
                  ? 'Se guardará como una serie programada (visible en "Recurrentes" y proyectada en el Calendario) en vez de un movimiento puntual.'
                  : 'It will be saved as a scheduled series (visible in "Recurring" and projected on the Calendar) instead of a one-time movement.'}
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="frecuencia-tx-label">{language === 'es' ? 'Frecuencia' : 'Frequency'}</InputLabel>
                <Select
                  labelId="frecuencia-tx-label"
                  name="frecuencia"
                  value={nuevaTx.frecuencia}
                  label={language === 'es' ? 'Frecuencia' : 'Frequency'}
                  onChange={handleChange}
                >
                  {frecuenciasRecurrente.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {nuevaTx.frecuencia === 'mensual' && (
                <TextField
                  margin="dense"
                  name="dia_mes"
                  label={language === 'es' ? 'Día del mes (1-31)' : 'Day of the month (1-31)'}
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 31 }}
                  value={nuevaTx.dia_mes}
                  onChange={handleChange}
                  helperText={language === 'es' ? 'El día en que se recibe el ingreso o se paga el egreso cada mes.' : 'The day the income is received or the expense is paid each month.'}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNew}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleCreateTx} variant="contained" color="primary" disabled={!nuevaTx.monto}>
            {language === 'es' ? 'Guardar' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Diálogo editar transacción */}
      <Dialog open={openEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          {transfEdit
            ? (language === 'es' ? 'Editar Transferencia' : 'Edit Transfer')
            : (language === 'es' ? 'Editar Transacción' : 'Edit Transaction')}
        </DialogTitle>
        <DialogContent>
          {/* Editor de transferencia: los dos lados a la vez. */}
          {txEditando && transfEdit && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {language === 'es'
                  ? 'Esto es una transferencia entre tus cuentas: son dos movimientos enlazados y se editan juntos. No suma en tus ingresos ni en tus gastos, solo mueve los saldos.'
                  : 'This is a transfer between your accounts: two linked entries, edited together. It does not count as income or spending — it only moves balances.'}
              </Alert>
              {convError && <Alert severity="error" sx={{ mb: 2 }}>{convError}</Alert>}
              <TextField
                fullWidth
                type="date"
                label={language === 'es' ? 'Fecha' : 'Date'}
                InputLabelProps={{ shrink: true }}
                value={transfEdit.fecha}
                onChange={(e) => setTransfEdit((p) => ({ ...p, fecha: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="transf-origen-label">{language === 'es' ? 'Sale de' : 'From'}</InputLabel>
                <Select
                  labelId="transf-origen-label"
                  label={language === 'es' ? 'Sale de' : 'From'}
                  value={transfEdit.cuentaOrigenId}
                  onChange={(e) => {
                    const cuenta = cuentas.find((c) => c.id === e.target.value);
                    setTransfEdit((p) => ({
                      ...p,
                      cuentaOrigenId: e.target.value,
                      monedaOrigen: (cuenta?.moneda || 'PEN').toUpperCase(),
                    }));
                  }}
                >
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id} disabled={c.id === transfEdit.cuentaDestinoId}>
                      {c.nombre}{c.moneda ? ` (${c.moneda})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label={language === 'es'
                  ? `Monto que sale (${transfEdit.monedaOrigen})`
                  : `Amount leaving (${transfEdit.monedaOrigen})`}
                value={transfEdit.monto}
                onChange={(e) => setTransfEdit((p) => ({
                  ...p,
                  monto: e.target.value,
                  // Misma moneda: los dos lados son el mismo número y
                  // dejarlos escribir dos distintos solo invita al error.
                  montoDestino: p.monedaOrigen === p.monedaDestino ? e.target.value : p.montoDestino,
                }))}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="transf-destino-label">{language === 'es' ? 'Entra a' : 'To'}</InputLabel>
                <Select
                  labelId="transf-destino-label"
                  label={language === 'es' ? 'Entra a' : 'To'}
                  value={transfEdit.cuentaDestinoId}
                  // La moneda que se muestra es la del PAGO, no la de la
                  // cuenta: si no, una tarjeta registrada en soles que
                  // recibió dólares aparece como "(PEN)" y parece un error.
                  renderValue={(id) => {
                    const c = cuentas.find((x) => x.id === id);
                    if (!c) return '';
                    return `${c.nombre} (${transfEdit.monedaDestino})`;
                  }}
                  onChange={(e) => {
                    const cuenta = cuentas.find((c) => c.id === e.target.value);
                    const esTarjeta = cuenta?.tipo === 'tarjeta_credito';
                    const moneda = (cuenta?.moneda || 'PEN').toUpperCase();
                    setTransfEdit((p) => ({
                      ...p,
                      cuentaDestinoId: e.target.value,
                      monedaDestino: moneda,
                      destinoEsTarjeta: esTarjeta,
                      monedasTarjeta: esTarjeta ? p.monedasTarjeta : [],
                      montoDestino: p.monedaOrigen === moneda ? p.monto : p.montoDestino,
                    }));
                  }}
                >
                  {cuentas.map((c) => (
                    <MenuItem key={c.id} value={c.id} disabled={c.id === transfEdit.cuentaOrigenId}>
                      {c.nombre}{c.moneda ? ` (${c.moneda})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="transf-tipo-label">
                  {language === 'es' ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                </InputLabel>
                <Select
                  labelId="transf-tipo-label"
                  label={language === 'es' ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                  value={transfEdit.transferenciaTipo || ''}
                  onChange={(e) => setTransfEdit((p) => ({ ...p, transferenciaTipo: e.target.value }))}
                >
                  <MenuItem value="">{language === 'es' ? 'Detectar automáticamente' : 'Detect automatically'}</MenuItem>
                  <MenuItem value="entre_cuentas">{language === 'es' ? 'Entre mis cuentas' : 'Between my accounts'}</MenuItem>
                  <MenuItem value="pago_tarjeta">{language === 'es' ? 'Pago de tarjeta' : 'Card payment'}</MenuItem>
                  <MenuItem value="cambio_moneda">{language === 'es' ? 'Cambio de moneda' : 'Currency exchange'}</MenuItem>
                </Select>
              </FormControl>
              {/* 21 sep 2026 (Augusto: "no me gusta cómo está esto. Debe
                  decir de qué cuenta ahorros o cuenta corriente, Soles o
                  Dólares proviene este pago de tarjeta de crédito").
                  Preguntaba "¿qué deuda se paga?" a secas, sin decir de
                  dónde sale la plata -- que es justo el dato con el que
                  uno contesta. Ahora la pregunta nombra la cuenta de
                  origen y su moneda, y solo aparece cuando la tarjeta
                  debe en más de una moneda: si debe en una sola no hay
                  nada que elegir, y ofrecer la otra solo invita a
                  equivocarse. */}
              {transfEdit.destinoEsTarjeta && (transfEdit.monedasTarjeta?.length || 0) > 1 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="transf-moneda-tarjeta-label">
                    {language === 'es' ? '¿Qué deuda abona este pago?' : 'Which balance does this payment go to?'}
                  </InputLabel>
                  <Select
                    labelId="transf-moneda-tarjeta-label"
                    label={language === 'es' ? '¿Qué deuda abona este pago?' : 'Which balance does this payment go to?'}
                    value={transfEdit.monedaDestino}
                    onChange={(e) => setTransfEdit((p) => ({
                      ...p,
                      monedaDestino: e.target.value,
                      montoDestino: p.monedaOrigen === e.target.value ? p.monto : p.montoDestino,
                    }))}
                  >
                    {transfEdit.monedasTarjeta.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m === 'USD'
                          ? (language === 'es' ? 'La deuda en dólares (USD)' : 'The dollar balance (USD)')
                          : (language === 'es' ? 'La deuda en soles (PEN)' : 'The soles balance (PEN)')}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(() => {
                      const cuentaOrigen = cuentas.find((c) => c.id === transfEdit.cuentaOrigenId);
                      const nombreOrigen = cuentaOrigen
                        ? `${cuentaOrigen.nombre} (${transfEdit.monedaOrigen})`
                        : transfEdit.monedaOrigen;
                      const mismaMoneda = transfEdit.monedaOrigen === transfEdit.monedaDestino;
                      if (language === 'es') {
                        return mismaMoneda
                          ? `El dinero sale de ${nombreOrigen} y abona la deuda en la misma moneda.`
                          : `El dinero sale de ${nombreOrigen} y abona una deuda en ${transfEdit.monedaDestino}: es un cambio de moneda, por eso te pide el monto que llegó.`;
                      }
                      return mismaMoneda
                        ? `The money leaves ${nombreOrigen} and pays the balance in the same currency.`
                        : `The money leaves ${nombreOrigen} and pays a ${transfEdit.monedaDestino} balance: that is a currency exchange, which is why the arriving amount is required.`;
                    })()}
                  </FormHelperText>
                </FormControl>
              )}
              {/* El monto del otro lado solo se pide cuando las monedas
                  difieren: ahí es un dato del banco, no una cuenta que
                  PCM pueda hacer con un tipo de cambio inventado. */}
              {transfEdit.monedaOrigen !== transfEdit.monedaDestino && (
                <TextField
                  fullWidth
                  type="number"
                  label={language === 'es'
                    ? `Monto que llega (${transfEdit.monedaDestino})`
                    : `Amount arriving (${transfEdit.monedaDestino})`}
                  value={transfEdit.montoDestino}
                  onChange={(e) => setTransfEdit((p) => ({ ...p, montoDestino: e.target.value }))}
                  helperText={language === 'es'
                    ? 'Las cuentas están en monedas distintas: pon el monto real tal como figura en el banco.'
                    : 'The accounts use different currencies: enter the real amount as the bank shows it.'}
                  sx={{ mb: 2 }}
                />
              )}
            </Box>
          )}

          {txEditando && !transfEdit && (
            <>
              <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                <InputLabel id="tipo-tx-edit-label">{language === 'es' ? 'Tipo' : 'Type'}</InputLabel>
                <Select
                  labelId="tipo-tx-edit-label"
                  name="tipo"
                  value={txEditando.tipo}
                  label={language === 'es' ? 'Tipo' : 'Type'}
                  onChange={handleEditChange}
                >
                  <MenuItem value="ingreso">{language === 'es' ? 'Ingreso' : 'Income'}</MenuItem>
                  <MenuItem value="gasto">{language === 'es' ? 'Egreso' : 'Expense'}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                name="monto"
                label={language === 'es' ? 'Monto' : 'Amount'}
                type="number"
                fullWidth
                value={txEditando.monto}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                freeSolo
                options={proveedoresConocidos}
                value={txEditando.proveedor || ''}
                onChange={handleProveedorChange(setTxEditando)}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') handleProveedorChange(setTxEditando)(e, value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    label={language === 'es' ? 'Proveedor (opcional)' : 'Merchant (optional)'}
                    helperText={language === 'es'
                      ? 'Ej. Wong, Uber, Netflix — si ya lo usaste antes, se auto-completa la categoría.'
                      : "E.g. Wong, Uber, Netflix — if you've used it before, the category auto-fills."}
                    fullWidth
                  />
                )}
                sx={{ mb: 2 }}
              />
              <CategoriaPicker
                value={txEditando.categoria || 'Otro'}
                onChange={(cat) => setTxEditando((prev) => ({ ...prev, categoria: cat }))}
                language={language}
              />
              <Autocomplete
                freeSolo
                options={allCategorias}
                value={txEditando.categoria || 'Otro'}
                onChange={(e, value) => setTxEditando((prev) => ({ ...prev, categoria: value || '' }))}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') setTxEditando((prev) => ({ ...prev, categoria: value }));
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: 8 }}>{getCategoryEmoji(option, emojiOverrides)}</span>
                    {traducirCategoria(option, language)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} margin="dense" label={language === 'es' ? 'Categoría' : 'Category'} fullWidth />
                )}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cuenta-tx-edit-label">{language === 'es' ? 'Cuenta (opcional)' : 'Account (optional)'}</InputLabel>
                <Select
                  labelId="cuenta-tx-edit-label"
                  name="account_id"
                  value={txEditando.account_id}
                  label={language === 'es' ? 'Cuenta (opcional)' : 'Account (optional)'}
                  onChange={handleEditChange}
                >
                  <MenuItem value="">{language === 'es' ? 'Sin asociar' : 'Not linked'}</MenuItem>
                  {cuentas.map((c) => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                  <MenuItem value="__crear_cuenta__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    + {language === 'es' ? 'Agregar cuenta nueva' : 'Add new account'}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                name="descripcion"
                label={language === 'es' ? 'Descripción' : 'Description'}
                type="text"
                fullWidth
                value={txEditando.descripcion || ''}
                onChange={handleEditChange}
                sx={{ mb: 2 }}
              />
              {/* 30 ago 2026 (pedido de Augusto): igual que "Agregar
                  Transacción", pero sin reemplazar esta transacción -- se
                  guarda tal cual Y, si se marca, se crea además una serie
                  recurrente hacia adelante (ver handleUpdateTxSubmit). */}
              <FormControlLabel
                control={
                  <Switch
                    checked={txEditando.crear_recurrente}
                    onChange={(e) => setTxEditando((prev) => ({ ...prev, crear_recurrente: e.target.checked }))}
                  />
                }
                label={language === 'es' ? '¿Convertir en recurrente?' : 'Convert to recurring?'}
              />
              {txEditando.crear_recurrente && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {language === 'es'
                      ? 'Esta transacción se guarda igual, y además se crea una serie programada (visible en "Recurrentes" y proyectada en el Calendario) hacia adelante.'
                      : 'This transaction is saved as usual, and a scheduled series (visible in "Recurring" and projected on the Calendar) is also created going forward.'}
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="frecuencia-tx-edit-label">{language === 'es' ? 'Frecuencia' : 'Frequency'}</InputLabel>
                    <Select
                      labelId="frecuencia-tx-edit-label"
                      name="frecuencia"
                      value={txEditando.frecuencia}
                      label={language === 'es' ? 'Frecuencia' : 'Frequency'}
                      onChange={handleEditChange}
                    >
                      {frecuenciasRecurrente.map((f) => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {txEditando.frecuencia === 'mensual' && (
                    <TextField
                      margin="dense"
                      name="dia_mes"
                      label={language === 'es' ? 'Día del mes (1-31)' : 'Day of the month (1-31)'}
                      type="number"
                      fullWidth
                      inputProps={{ min: 1, max: 31 }}
                      value={txEditando.dia_mes}
                      onChange={handleEditChange}
                      helperText={language === 'es' ? 'El día en que se recibe el ingreso o se paga el egreso cada mes.' : 'The day the income is received or the expense is paid each month.'}
                    />
                  )}
                </Box>
              )}

              {/* 21 sep 2026 (Augusto: "trato de editar una transacción y
                  no me aparece Transferencia, solo ingreso egreso").
                  Va acá abajo y cerrado por defecto porque no es un campo
                  más del movimiento: es cambiarle la naturaleza, y arma un
                  segundo movimiento en la otra cuenta. */}
              {!esTransferencia(txEditando) && txEditando.account_id && (
                <>
                  <Divider sx={{ my: 2 }} />
                  {!convAbierto ? (
                    <Button size="small" onClick={() => setConvAbierto(true)}>
                      {language === 'es'
                        ? '¿En realidad fue una transferencia entre tus cuentas?'
                        : 'Was this actually a transfer between your accounts?'}
                    </Button>
                  ) : (
                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {language === 'es'
                          ? (txEditando.tipo === 'gasto'
                            ? 'Este egreso no fue un gasto: el dinero se movió a otra cuenta tuya. Elige a cuál y PCM registra el otro lado.'
                            : 'Este ingreso no fue un ingreso nuevo: el dinero vino de otra cuenta tuya. Elige de cuál y PCM registra el otro lado.')
                          : (txEditando.tipo === 'gasto'
                            ? 'This expense was not spending: the money moved to another account of yours. Pick which one and PCM records the other side.'
                            : 'This income was not new money: it came from another account of yours. Pick which one and PCM records the other side.')}
                      </Typography>
                      {convError && <Alert severity="error" sx={{ mb: 1.5 }}>{convError}</Alert>}
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <InputLabel id="conv-contraparte-label">
                          {language === 'es'
                            ? (txEditando.tipo === 'gasto' ? '¿A qué cuenta fue?' : '¿De qué cuenta vino?')
                            : (txEditando.tipo === 'gasto' ? 'Which account did it go to?' : 'Which account did it come from?')}
                        </InputLabel>
                        <Select
                          labelId="conv-contraparte-label"
                          label={language === 'es'
                            ? (txEditando.tipo === 'gasto' ? '¿A qué cuenta fue?' : '¿De qué cuenta vino?')
                            : (txEditando.tipo === 'gasto' ? 'Which account did it go to?' : 'Which account did it come from?')}
                          value={convContraparte}
                          onChange={(e) => { setConvContraparte(e.target.value); setConvMonto(''); }}
                        >
                          {cuentas
                            .filter((c) => c.id !== txEditando.account_id)
                            .map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.nombre}{c.moneda ? ` (${c.moneda})` : ''}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {/* Si las monedas no coinciden, el monto del otro
                          lado es un dato que solo tiene el banco -- no se
                          adivina con un tipo de cambio inventado. */}
                      {(() => {
                        const destino = cuentas.find((c) => c.id === convContraparte);
                        const monedaDestino = (destino?.moneda || 'PEN').toUpperCase();
                        const distinta = destino && monedaDestino !== monedaDeTx(txEditando);
                        if (!distinta) return null;
                        return (
                          <TextField
                            size="small"
                            fullWidth
                            type="number"
                            label={language === 'es'
                              ? `¿Cuánto ${txEditando.tipo === 'gasto' ? 'llegó' : 'salió'} en ${monedaDestino}?`
                              : `How much ${txEditando.tipo === 'gasto' ? 'arrived' : 'left'} in ${monedaDestino}?`}
                            value={convMonto}
                            onChange={(e) => setConvMonto(e.target.value)}
                            helperText={language === 'es'
                              ? 'Las cuentas están en monedas distintas: pon el monto real del otro lado, tal como figura en el banco.'
                              : 'The accounts use different currencies: enter the real amount on the other side, as the bank shows it.'}
                            sx={{ mb: 1.5 }}
                          />
                        );
                      })()}
                      {/* 21 sep 2026 (Augusto: "cuando se transforma un
                          ingreso o egreso en transferencia, y me pide la
                          categoría, esta categoría no se adopta al
                          terminar la entrada").
                          Y no puede adoptarse: al convertir, la categoría
                          pasa a ser "Transferencia" obligatoriamente --
                          ese valor es lo que hace que el movimiento deje
                          de contar como gasto o ingreso. Lo que sí se
                          conserva es la intención, y va acá. */}
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <InputLabel id="conv-tipo-label">
                          {language === 'es' ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                        </InputLabel>
                        <Select
                          labelId="conv-tipo-label"
                          label={language === 'es' ? '¿Qué tipo de transferencia es?' : 'What kind of transfer is this?'}
                          value={convTipo}
                          onChange={(e) => setConvTipo(e.target.value)}
                        >
                          <MenuItem value="">{language === 'es' ? 'Detectar automáticamente' : 'Detect automatically'}</MenuItem>
                          <MenuItem value="entre_cuentas">{language === 'es' ? 'Entre mis cuentas' : 'Between my accounts'}</MenuItem>
                          <MenuItem value="pago_tarjeta">{language === 'es' ? 'Pago de tarjeta' : 'Card payment'}</MenuItem>
                          <MenuItem value="cambio_moneda">{language === 'es' ? 'Cambio de moneda' : 'Currency exchange'}</MenuItem>
                        </Select>
                        <FormHelperText>
                          {language === 'es'
                            ? 'La categoría pasa a ser "Transferencia": es lo que hace que deje de contar como gasto o ingreso. Esto guarda de qué clase de transferencia se trata.'
                            : 'The category becomes "Transfer" — that is what stops it counting as spending or income. This records what kind of transfer it was.'}
                        </FormHelperText>
                      </FormControl>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!convContraparte || convCargando}
                          onClick={handleConvertirEnTransferencia}
                        >
                          {language === 'es' ? 'Convertir en transferencia' : 'Convert to transfer'}
                        </Button>
                        <Button size="small" onClick={() => setConvAbierto(false)} disabled={convCargando}>
                          {language === 'es' ? 'Cancelar' : 'Cancel'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            onClick={transfEdit ? handleGuardarTransferencia : handleUpdateTxSubmit}
            variant="contained"
            color="primary"
            disabled={transfEdit ? convCargando : false}
          >
            {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 28 ago 2026 (pedido de Augusto): alta rápida de cuenta desde el
          selector "Cuenta (opcional)" de Agregar/Editar Transacción, sin
          salir de esta página. Versión reducida del formulario de Cuentas
          > Nueva Cuenta -- el backend solo exige `nombre` (ver
          schemas/account.py), así que se omiten Entidad Bancaria y la
          vista previa de ícono para no alargar el flujo. */}
      <Dialog open={openNuevaCuentaRapida} onClose={() => setOpenNuevaCuentaRapida(false)} fullWidth maxWidth="xs">
        <DialogTitle>{language === 'es' ? 'Agregar cuenta nueva' : 'Add new account'}</DialogTitle>
        <DialogContent>
          {errorCuentaRapida && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorCuentaRapida}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="nombre"
            label={language === 'es' ? 'Nombre de la cuenta' : 'Account name'}
            placeholder={language === 'es' ? 'Ej. BCP Ahorros, Visa Interbank' : 'e.g. Chase Checking'}
            type="text"
            fullWidth
            value={nuevaCuentaRapida.nombre}
            onChange={handleNuevaCuentaRapidaChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="tipo-cuenta-rapida-label">{language === 'es' ? 'Tipo de Cuenta' : 'Account Type'}</InputLabel>
            <Select
              labelId="tipo-cuenta-rapida-label"
              name="tipo"
              value={nuevaCuentaRapida.tipo}
              label={language === 'es' ? 'Tipo de Cuenta' : 'Account Type'}
              onChange={handleNuevaCuentaRapidaChange}
            >
              <MenuItem value="ahorros">{language === 'es' ? 'Cuenta de Ahorros' : 'Savings Account'}</MenuItem>
              <MenuItem value="corriente">{language === 'es' ? 'Cuenta Corriente' : 'Checking Account'}</MenuItem>
              <MenuItem value="efectivo">{language === 'es' ? 'Efectivo' : 'Cash'}</MenuItem>
              <MenuItem value="tarjeta_credito">{language === 'es' ? 'Tarjeta de Crédito' : 'Credit Card'}</MenuItem>
              <MenuItem value="otro">{language === 'es' ? 'Otro' : 'Other'}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="moneda-cuenta-rapida-label">{language === 'es' ? 'Moneda' : 'Currency'}</InputLabel>
            <Select
              labelId="moneda-cuenta-rapida-label"
              name="moneda"
              value={nuevaCuentaRapida.moneda}
              label={language === 'es' ? 'Moneda' : 'Currency'}
              onChange={handleNuevaCuentaRapidaChange}
            >
              <MenuItem value="PEN">{language === 'es' ? 'Sol Peruano (S/)' : 'Peruvian Sol (S/)'}</MenuItem>
              <MenuItem value="USD">{language === 'es' ? 'Dólar Americano ($)' : 'US Dollar ($)'}</MenuItem>
              <MenuItem value="EUR">{language === 'es' ? 'Euro (€)' : 'Euro (€)'}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="balance"
            label={language === 'es' ? 'Balance Inicial' : 'Initial Balance'}
            type="number"
            fullWidth
            value={nuevaCuentaRapida.balance}
            onChange={handleNuevaCuentaRapidaChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNuevaCuentaRapida(false)} disabled={guardandoCuentaRapida}>
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={handleGuardarCuentaRapida}
            variant="contained"
            color="primary"
            disabled={!nuevaCuentaRapida.nombre.trim() || guardandoCuentaRapida}
          >
            {guardandoCuentaRapida ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Guardar' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación */}
      <RecurringCandidatesDialog
        open={recurringDialogOpen}
        candidatos={recurringCandidatos}
        onConfirm={handleConfirmRecurringCandidates}
        onCancel={handleCancelRecurringCandidates}
      />
      <Dialog open={openDelete} onClose={handleDeleteConfirmClose}>
        <DialogTitle>{language === 'es' ? '¿Eliminar transacción?' : 'Delete transaction?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {language === 'es'
              ? '¿Estás seguro de que deseas eliminar esta transacción? Esta acción no se puede deshacer.'
              : 'Are you sure you want to delete this transaction? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleDeleteTx} color="error" autoFocus>
            {language === 'es' ? 'Eliminar' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 12 ago 2026: confirmación de borrado masivo -- mismo patrón que
          el borrado individual de arriba, solo que muestra cuántas se van
          a eliminar. */}
      <Dialog open={openBulkDelete} onClose={handleBulkDeleteClose}>
        <DialogTitle>{language === 'es' ? `¿Eliminar ${seleccionadas.size} transacciones?` : `Delete ${seleccionadas.size} transactions?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {language === 'es' ? 'Esta acción no se puede deshacer.' : 'This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteClose} disabled={bulkDeleting}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleBulkDelete} color="error" disabled={bulkDeleting} autoFocus>
            {bulkDeleting ? (language === 'es' ? 'Eliminando...' : 'Deleting...') : (language === 'es' ? 'Eliminar' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 30 ago 2026 (pedido de Augusto): convertir en masa las
          transacciones seleccionadas en series recurrentes -- crea una
          serie nueva por cada una (ver handleBulkRecurring), las
          transacciones originales no se tocan. Una sola frecuencia se
          aplica a todas las seleccionadas. */}
      <Dialog open={openBulkRecurring} onClose={handleBulkRecurringClose} fullWidth maxWidth="xs">
        <DialogTitle>
          {language === 'es' ? `¿Marcar ${seleccionadas.size} transacciones como recurrentes?` : `Mark ${seleccionadas.size} transactions as recurring?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {language === 'es'
              ? 'Se crea una serie recurrente nueva a partir de cada transacción seleccionada (visibles en "Recurrentes" y proyectadas en el Calendario). Las transacciones originales no cambian.'
              : 'A new recurring series is created from each selected transaction (visible in "Recurring" and projected on the Calendar). The original transactions don\'t change.'}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="frecuencia-bulk-label">{language === 'es' ? 'Frecuencia' : 'Frequency'}</InputLabel>
            <Select
              labelId="frecuencia-bulk-label"
              value={bulkRecurringFrecuencia}
              label={language === 'es' ? 'Frecuencia' : 'Frequency'}
              onChange={(e) => setBulkRecurringFrecuencia(e.target.value)}
            >
              {frecuenciasRecurrente.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkRecurringClose} disabled={bulkRecurringSaving}>{language === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={handleBulkRecurring} variant="contained" disabled={bulkRecurringSaving} autoFocus>
            {bulkRecurringSaving ? (language === 'es' ? 'Creando...' : 'Creating...') : (language === 'es' ? 'Marcar como recurrente' : 'Mark as recurring')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 17 sep 2026: se quitaron de acá los diálogos de "Duplicar mes
          anterior" y "Unificar categorías duplicadas" junto con sus
          botones -- ver comentario junto al estado/handlers más arriba. */}
      {/* 28 ago 2026 (pedido de Augusto: "se duplicaron las entradas...
          bórrame las duplicadas") -- confirmación de "Eliminar
          transacciones duplicadas", llama a POST
          /transactions/deduplicate (ver handleConfirmDedup) y muestra
          cuántas se borraron, con ejemplos concretos. */}
      {/* 17 sep 2026: borrado masivo de transacciones. El diálogo muestra
          el conteo real antes de pedir la palabra de confirmación, y deja
          elegir entre lo filtrado y todo -- ver handleOpenBorrarTodas. */}
      <Dialog open={borrarTodasOpen} onClose={() => setBorrarTodasOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {language === 'es' ? 'Borrar transacciones' : 'Delete transactions'}
        </DialogTitle>
        <DialogContent>
          {borrarTodasResultado ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              {language === 'es'
                ? `Listo — se eliminaron ${borrarTodasResultado.eliminadas} transacciones.`
                : `Done — ${borrarTodasResultado.eliminadas} transactions deleted.`}
            </Alert>
          ) : (
            <>
              {hayFiltrosDeBorrado && (
                <RadioGroup
                  value={borrarTodasAlcance}
                  onChange={(e) => handleCambiarAlcanceBorrado(e.target.value)}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel
                    value="filtrado"
                    control={<Radio size="small" />}
                    label={
                      language === 'es'
                        ? 'Solo lo que estoy viendo (según los filtros de cuenta y año)'
                        : 'Only what I am seeing (per the account and year filters)'
                    }
                  />
                  <FormControlLabel
                    value="todo"
                    control={<Radio size="small" />}
                    label={
                      language === 'es'
                        ? 'Todas mis transacciones'
                        : 'All my transactions'
                    }
                  />
                </RadioGroup>
              )}

              <Alert severity="warning" sx={{ mb: 2 }}>
                {language === 'es'
                  ? 'Esto no se puede deshacer. No se tocan tus cuentas, deudas, presupuestos, categorías ni metas — solo los movimientos.'
                  : 'This cannot be undone. Your accounts, debts, budgets, categories and goals are not touched — only the transactions.'}
              </Alert>

              {borrarTodasPreview ? (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {language === 'es'
                    ? `Se van a eliminar ${borrarTodasPreview.count} transacciones.`
                    : `${borrarTodasPreview.count} transactions will be deleted.`}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {language === 'es' ? 'Calculando…' : 'Calculating…'}
                </Typography>
              )}

              <TextField
                fullWidth
                size="small"
                label={
                  language === 'es'
                    ? 'Escribe BORRAR para confirmar'
                    : 'Type BORRAR to confirm'
                }
                value={borrarTodasPalabra}
                onChange={(e) => setBorrarTodasPalabra(e.target.value)}
                disabled={!borrarTodasPreview || borrarTodasPreview.count === 0}
              />

              {borrarTodasError && (
                <Alert severity="error" sx={{ mt: 2 }}>{borrarTodasError}</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrarTodasOpen(false)}>
            {borrarTodasResultado
              ? (language === 'es' ? 'Cerrar' : 'Close')
              : (language === 'es' ? 'Cancelar' : 'Cancel')}
          </Button>
          {!borrarTodasResultado && (
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmBorrarTodas}
              disabled={
                borrarTodasLoading ||
                !borrarTodasPreview ||
                borrarTodasPreview.count === 0 ||
                borrarTodasPalabra.trim().toUpperCase() !== 'BORRAR'
              }
            >
              {borrarTodasLoading
                ? (language === 'es' ? 'Borrando…' : 'Deleting…')
                : (language === 'es' ? 'Borrar' : 'Delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={dedupDialogOpen} onClose={handleCloseDedupDialog} fullWidth maxWidth="xs">
        <DialogTitle>{language === 'es' ? 'Eliminar transacciones duplicadas' : 'Delete duplicate transactions'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {language === 'es'
              ? 'Busca transacciones con la misma cuenta, tipo, monto, moneda y fecha exacta (coincidencia exacta, no aproximada) -- suele pasar al reintentar una importación que ya se había completado antes. Conserva la más antigua de cada grupo y borra el resto.'
              : 'Looks for transactions with the same account, type, amount, currency and exact date (exact match, not approximate) -- usually happens when retrying an import that had already completed. Keeps the oldest of each group and deletes the rest.'}
          </Typography>
          {dedupError && <Alert severity="error" sx={{ mb: 2 }}>{dedupError}</Alert>}
          {dedupResult && (
            <Alert severity={dedupResult.transacciones_eliminadas > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
              {dedupResult.transacciones_eliminadas > 0 ? (
                <>
                  {language === 'es' ? (
                    <>Se eliminaron <strong>{dedupResult.transacciones_eliminadas}</strong> transacciones duplicadas ({dedupResult.grupos_con_duplicados} grupos).</>
                  ) : (
                    <><strong>{dedupResult.transacciones_eliminadas}</strong> duplicate transactions were deleted ({dedupResult.grupos_con_duplicados} groups).</>
                  )}
                  {dedupResult.ejemplos.length > 0 && (
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                      {dedupResult.ejemplos.map((ej, idx) => (
                        <li key={idx}>
                          <Typography variant="caption">
                            {ej.proveedor || (language === 'es' ? 'Sin proveedor' : 'No merchant')} —{' '}
                            {(ej.moneda || '').toUpperCase() === 'USD' ? '$' : 'S/'} {formatAmountRaw(ej.monto)} —{' '}
                            {new Date(ej.fecha).toLocaleDateString(language === 'es' ? 'es-PE' : 'en-US')} ({ej.duplicados_eliminados}{' '}
                            {language === 'es'
                              ? (ej.duplicados_eliminados === 1 ? 'duplicado' : 'duplicados')
                              : (ej.duplicados_eliminados === 1 ? 'duplicate' : 'duplicates')})
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                language === 'es' ? 'No se encontró ninguna transacción duplicada.' : 'No duplicate transactions were found.'
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDedupDialog}>{dedupResult ? (language === 'es' ? 'Cerrar' : 'Close') : (language === 'es' ? 'Cancelar' : 'Cancel')}</Button>
          {!dedupResult && (
            <Button onClick={handleConfirmDedup} variant="contained" color="error" disabled={dedupLoading}>
              {dedupLoading ? (language === 'es' ? 'Buscando...' : 'Searching...') : (language === 'es' ? 'Eliminar duplicadas' : 'Delete duplicates')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};
export default TransactionsPage;
